/** Stable question / option ids — saved in answers JSON (language-independent). */

export const SURVEY_LANGS = ['mr', 'hi', 'en'];

const HINDI_FAMILY = new Set([
  'hi', 'bho', 'raj', 'cg', 'har', 'mai', 'doi', 'ne', 'kok', 'ur',
]);

/** Map app language to nearest survey bundle (mr / hi / en). */
export function resolveSurveyLang(code) {
  const normalized = String(code || 'mr').toLowerCase();
  if (SURVEY_LANGS.includes(normalized)) return normalized;
  if (HINDI_FAMILY.has(normalized)) return 'hi';
  return 'en';
}

export const OWNER_QUESTION_IDS = [
  'ownerType',
  'machines_count',
  'machine_types',
  'jcb_3dx_rental_rate',
  'excavator_rental_rate',
  'mini_excavator_rental_rate',
  'inf_emi_blocked',
  'inf_diesel_theft',
  'inf_side_booking',
  'singlePain1',
  'singlePain2',
  'singlePain3',
  'fleetPain1',
  'fleetPain2',
  'fleetPain3',
  'contractorOwnerPain1',
  'contractorOwnerPain2',
  'contractorOwnerPain3',
  'uses_whatsapp',
  'trial_interest',
];

export const CLIENT_QUESTION_IDS = [
  'clientProfession',
  'hire_frequency',
  'occupation',
  'machine_types_needed',
  'jcb_3dx_pay_rate',
  'excavator_pay_rate',
  'mini_excavator_pay_rate',
  'inf_budget_creep',
  'inf_fuel_surcharge',
  'inf_rounding_loss',
  'inf_cash_crunch',
  'inf_slow_progress',
  'inf_punctuality',
  'inf_calls',
  'inf_reliability',
  'inf_advance_trust',
  'inf_live_map',
  'inf_digital_ledger',
  'farmerPain1',
  'farmerPain2',
  'farmerPain3',
  'roadPain1',
  'roadPain2',
  'roadPain3',
  'industrialPain1',
  'industrialPain2',
  'industrialPain3',
  'govtPain1',
  'govtPain2',
  'govtPain3',
  'builderPain1',
  'builderPain2',
  'builderPain3',
  'wellPain1',
  'wellPain2',
  'wellPain3',
  'uses_whatsapp',
  'trial_interest',
];

export const PM_QUESTION_IDS = [
  'pmProjectsCount',
  'pmSourceMethod',
  'pmBiggestChallenge',
  'pmBudgetRange',
  'pmDecisionAuthority',
  'uses_whatsapp',
];

export const CONTRACTOR_QUESTION_IDS = [
  'contractorProjectType',
  'contractorHireFrequency',
  'contractorPain',
  'contractorGst',
  'contractorPayment',
  'uses_whatsapp',
];

export const HOT_LEAD_QUESTION_ID = 'trial_interest';
export const HOT_LEAD_OPTION_ID = 'yes_definitely';

/** Questions stored as string[] in answers JSON (not single option id). */
export const MULTI_SELECT_QUESTION_IDS = ['machine_types'];

/** Owner rental-rate questions shown only when matching machine type is selected. */
export const MACHINE_TYPE_RENTAL_QUESTIONS = {
  jcb_3dx: 'jcb_3dx_rental_rate',
  excavator: 'excavator_rental_rate',
  mini_excavator: 'mini_excavator_rental_rate',
};

export const RENTAL_RATE_QUESTION_IDS = Object.values(MACHINE_TYPE_RENTAL_QUESTIONS);

/** Client pay-rate questions shown only for selected machine_types_needed. */
export const CLIENT_MACHINE_PAY_QUESTIONS = {
  jcb_3dx: 'jcb_3dx_pay_rate',
  excavator: 'excavator_pay_rate',
  mini_excavator: 'mini_excavator_pay_rate',
};

export const CLIENT_PAY_RATE_QUESTION_IDS = Object.values(CLIENT_MACHINE_PAY_QUESTIONS);

export const FOCUS_MACHINE_TYPE_OPTIONS_MR = [
  { id: 'jcb_3dx', text: 'JCB 3DX' },
  { id: 'excavator', text: 'Excavator / Poclain' },
  { id: 'mini_excavator', text: 'Mini Excavator' },
];

export const FOCUS_MACHINE_TYPE_OPTIONS_HI = [
  { id: 'jcb_3dx', text: 'JCB 3DX' },
  { id: 'excavator', text: 'Excavator / Poclain' },
  { id: 'mini_excavator', text: 'Mini Excavator' },
];

export const FOCUS_MACHINE_TYPE_OPTIONS_EN = [
  { id: 'jcb_3dx', text: 'JCB 3DX' },
  { id: 'excavator', text: 'Excavator / Poclain' },
  { id: 'mini_excavator', text: 'Mini Excavator' },
];

const whatsappOptionsMr = [
  { id: 'yes_daily', text: 'हो, रोज वापरतो' },
  { id: 'sometimes', text: 'कधीकधी' },
  { id: 'rarely', text: 'क्वचितच' },
  { id: 'no', text: 'नाही' },
];

const whatsappOptionsHi = [
  { id: 'yes_daily', text: 'हाँ, रोज़ इस्तेमाल करता/करती हूँ' },
  { id: 'sometimes', text: 'कभी-कभी' },
  { id: 'rarely', text: 'बहुत कम' },
  { id: 'no', text: 'नहीं' },
];

const whatsappOptionsEn = [
  { id: 'yes_daily', text: 'Yes, daily' },
  { id: 'sometimes', text: 'Sometimes' },
  { id: 'rarely', text: 'Rarely' },
  { id: 'no', text: 'No' },
];

const dieselTheftOptionsMr = [
  { id: 'theft_yes', text: 'होय रं बाबा! नेहमीच डिझेल कमी पडतंय, नाहीतर चोरी होतंय.' },
  { id: 'theft_suspect', text: 'संशय हाय, पण ऑपरेटर थेट डोळ्यासमोर नसल्यानं काय बोलता येत नाही.' },
  { id: 'theft_no', text: 'नाय, माझा ऑपरेटर एकदम विश्वासू हाय, हिशोब चोख असतोय त्याचा.' },
];
const dieselTheftOptionsHi = [
  { id: 'theft_yes', text: 'हाँ, अक्सर डीज़ल कम / चोरी' },
  { id: 'theft_suspect', text: 'शक — सीधा आरोप नहीं' },
  { id: 'theft_no', text: 'नहीं, ऑपरेटर भरोसेमंद' },
];
const dieselTheftOptionsEn = [
  { id: 'theft_yes', text: 'Yes — diesel often missing / stolen' },
  { id: 'theft_suspect', text: 'Suspect it — cannot blame operator' },
  { id: 'theft_no', text: 'No — operator trustworthy' },
];

const paymentDelayOptionsMr = [
  { id: 'months_chase', text: '१–३ महिने चकरा / उधारी' },
  { id: 'sometimes_lost', text: 'कधीकधी पैसे पूर्ण बुडतात' },
  { id: 'delay_15_30', text: '१५–३० दिवस उशीर' },
  { id: 'on_time_cash', text: 'रोख / लगेच पैसे' },
];
const paymentDelayOptionsHi = [
  { id: 'months_chase', text: '1–3 महीने चक्कर / बकाया' },
  { id: 'sometimes_lost', text: 'कभी पैसे डूब जाते' },
  { id: 'delay_15_30', text: '15–30 दिन देरी' },
  { id: 'on_time_cash', text: 'नकद / तुरंत' },
];
const paymentDelayOptionsEn = [
  { id: 'months_chase', text: '1–3 months chasing dues' },
  { id: 'sometimes_lost', text: 'Sometimes payment fully lost' },
  { id: 'delay_15_30', text: '15–30 days delay' },
  { id: 'on_time_cash', text: 'Cash / paid quickly' },
];

const sideBookingOptionsMr = [
  { id: 'yes_often', text: 'होय, अनेकदा तासांचा आणि डिझेलचा मेळ लागत नाय, मेंटेनन्सचा भुर्दंड सोसावा लागतोय.' },
  { id: 'hard_measure', text: 'ट्रॅक करणं अवघड हाय, मालक स्वतः किती वेळ साईटवर बसून राहणार?' },
  { id: 'no_honest', text: 'नाय, माझा ऑपरेटर इमानी हाय, गाडी स्वतःच्या जीवापाड सांभाळतोय.' },
];
const sideBookingOptionsHi = [
  { id: 'yes_often', text: 'हाँ — घंटे/डीज़ल match नहीं' },
  { id: 'hard_measure', text: 'मापना मुश्किल' },
  { id: 'no_honest', text: 'नहीं, ईमानदार' },
];
const sideBookingOptionsEn = [
  { id: 'yes_often', text: 'Yes — hours/diesel do not match' },
  { id: 'hard_measure', text: 'Hard to measure — not on site' },
  { id: 'no_honest', text: 'No — operator honest' },
];

const manualLedgerOptionsMr = [
  { id: 'yes_headache', text: 'होय, ऑपरेटरनं जेवढे तास सांगितले त्यावरच मान डोलवावी लागतीये, महिन्याला हिशोब जुळताना डोकं उठतंय.' },
  { id: 'trust_operator', text: 'सुपरवायझर किंवा ऑपरेटरवर विसंबून राहावं लागतंय, चुका होणारच.' },
  { id: 'no_accurate', text: 'नाय, आमचा डायरीचा हिशोब एकदम काटा असतोय.' },
];
const manualLedgerOptionsHi = [
  { id: 'yes_headache', text: 'हाँ — month-end match मुश्किल' },
  { id: 'trust_operator', text: 'ऑपरेटर पर भरोसा' },
  { id: 'no_accurate', text: 'नहीं, सही हिसाब' },
];
const manualLedgerOptionsEn = [
  { id: 'yes_headache', text: 'Yes — month-end reconciliation hard' },
  { id: 'trust_operator', text: 'Must trust operator' },
  { id: 'no_accurate', text: 'No — manual log accurate' },
];

const remoteLockOptionsMr = [
  { id: 'yes_control', text: 'हो — Full control on operator & payment' },
  { id: 'maybe_possible', text: 'फायदा — पण technically शक्य?' },
  { id: 'no_need', text: 'गरज नाही' },
];
const remoteLockOptionsHi = [
  { id: 'yes_control', text: 'हाँ — Full control' },
  { id: 'maybe_possible', text: 'फायदा — possible?' },
  { id: 'no_need', text: 'ज़रूरत नहीं' },
];
const remoteLockOptionsEn = [
  { id: 'yes_control', text: 'Yes — full control on operator & payment' },
  { id: 'maybe_possible', text: 'Would help — not sure if possible' },
  { id: 'no_need', text: 'No need' },
];

const punctualityOptionsMr = [
  { id: 'yes_loss', text: 'होय, मशीन मालक ऐनवेळी दगा देतात किंवा फोन बंद करतात, तिकडं मजुरांचा रोजचा पगार फुकट जातोय.' },
  { id: 'delay_1_2', text: '१-२ तास उशीर होणं किंवा मशीन रडत-खडत चालणं नेहमीचंच आहे.' },
  { id: 'no_on_time', text: 'नाय, मशिन्स नेहमी वेळेत येतात आणि एकदम ओक्के कँडिशनमध्ये असत्यात.' },
];
const punctualityOptionsHi = [
  { id: 'yes_loss', text: 'हाँ — late, काम रुकता' },
  { id: 'delay_1_2', text: '1–2 hr delay normal' },
  { id: 'no_on_time', text: 'हमेशा on time' },
];
const punctualityOptionsEn = [
  { id: 'yes_loss', text: 'Yes — late, work/labour stalls' },
  { id: 'delay_1_2', text: '1–2 hr delay normal' },
  { id: 'no_on_time', text: 'Always on time' },
];

const callsOptionsMr = [
  { id: 'lots_calls', text: 'खूप call + दर घासाघीस' },
  { id: 'some_time', text: 'थोडा वेळ, मशीन मिळते' },
  { id: 'fixed_contacts', text: 'fix contacts — tension नाही' },
];
const callsOptionsHi = [
  { id: 'lots_calls', text: 'बहुत call + rate घसीट' },
  { id: 'some_time', text: 'थोडा time' },
  { id: 'fixed_contacts', text: 'fix contacts' },
];
const callsOptionsEn = [
  { id: 'lots_calls', text: 'Many calls + negotiation' },
  { id: 'some_time', text: 'Some time, machine found' },
  { id: 'fixed_contacts', text: 'Fixed contacts enough' },
];

const reliabilityOptionsMr = [
  { id: 'yes_cancel', text: 'हो — जास्त दर मिळाला की cancel' },
  { id: 'rare', text: 'दुर्मिळ' },
  { id: 'no_trust', text: 'नाही, संबंध घट्ट' },
];
const reliabilityOptionsHi = [
  { id: 'yes_cancel', text: 'हाँ — ज़्यादा rate तो cancel' },
  { id: 'rare', text: 'कभी-कभार' },
  { id: 'no_trust', text: 'नहीं, strong relation' },
];
const reliabilityOptionsEn = [
  { id: 'yes_cancel', text: 'Yes — cancels for higher rate' },
  { id: 'rare', text: 'Rarely' },
  { id: 'no_trust', text: 'No — trusted relationship' },
];

const liveMapOptionsMr = [
  { id: 'yes_prep', text: 'हो — site prep सोपे (Zomato/Uber सारखे)' },
  { id: 'nice_not_must', text: 'पाहायला आवडेल, फार जरूरी नाही' },
  { id: 'phone_easier', text: 'फोन सोपे' },
];
const liveMapOptionsHi = [
  { id: 'yes_prep', text: 'हाँ — site prep (Zomato/Uber)' },
  { id: 'nice_not_must', text: 'nice, not must' },
  { id: 'phone_easier', text: 'phone easier' },
];
const liveMapOptionsEn = [
  { id: 'yes_prep', text: 'Yes — helps site prep (like Zomato/Uber)' },
  { id: 'nice_not_must', text: 'Nice, not essential' },
  { id: 'phone_easier', text: 'Easier to call' },
];

const jcb3dxRateOptionsMr = [
  { id: 'under_900', text: 'तासदर — ₹900 पेक्षा कमी' },
  { id: 'around_1000', text: 'तासदर — ₹900–₹1,100 (~₹1,000)' },
  { id: 'above_1100', text: 'तासदर — ₹1,100+' },
];

const excavatorRateOptionsMr = [
  { id: 'under_2000', text: 'तासदर — ₹2,000 पेक्षा कमी' },
  { id: 'market_2200_2800', text: 'तासदर — ₹2,200–₹2,800' },
  { id: 'above_2800', text: 'तासदर — ₹2,800+' },
];

const miniExcavatorRateOptionsMr = [
  { id: 'under_800', text: 'तासदर — ₹800 पेक्षा कमी' },
  { id: 'range_800_1000', text: 'तासदर — ₹800–₹1,000' },
  { id: 'above_1000', text: 'तासदर — ₹1,000+' },
];

const jcb3dxRateOptionsHi = [
  { id: 'under_900', text: 'प्रति घंटा — ₹900 से कम' },
  { id: 'around_1000', text: 'प्रति घंटा — ₹900–₹1,100 (~₹1,000)' },
  { id: 'above_1100', text: 'प्रति घंटा — ₹1,100+' },
];

const excavatorRateOptionsHi = [
  { id: 'under_2000', text: 'प्रति घंटा — ₹2,000 से कम' },
  { id: 'market_2200_2800', text: 'प्रति घंटा — ₹2,200–₹2,800' },
  { id: 'above_2800', text: 'प्रति घंटा — ₹2,800+' },
];

const miniExcavatorRateOptionsHi = [
  { id: 'under_800', text: 'प्रति घंटा — ₹800 से कम' },
  { id: 'range_800_1000', text: 'प्रति घंटा — ₹800–₹1,000' },
  { id: 'above_1000', text: 'प्रति घंटा — ₹1,000+' },
];

const jcb3dxRateOptionsEn = [
  { id: 'under_900', text: 'Hourly — Under ₹900/hr' },
  { id: 'around_1000', text: 'Hourly — ₹900–₹1,100 (~₹1,000/hr)' },
  { id: 'above_1100', text: 'Hourly — ₹1,100+/hr' },
];

const excavatorRateOptionsEn = [
  { id: 'under_2000', text: 'Hourly — Under ₹2,000/hr' },
  { id: 'market_2200_2800', text: 'Hourly — ₹2,200–₹2,800/hr' },
  { id: 'above_2800', text: 'Hourly — ₹2,800+/hr' },
];

const miniExcavatorRateOptionsEn = [
  { id: 'under_800', text: 'Hourly — Under ₹800/hr' },
  { id: 'range_800_1000', text: 'Hourly — ₹800–₹1,000/hr' },
  { id: 'above_1000', text: 'Hourly — ₹1,000+/hr' },
];

const ownerQuestionsMr = {
  ownerType: {
    label: 'मशीन मालक म्हणून तुमचे वर्णन कशाशी जुळते?',
    options: [
      { id: 'single', text: 'एकट्या मशीनचा मालक — १-२ मशीन, स्वतः चालवतो' },
      { id: 'fleet', text: 'फ्लीट मालक — ३ किंवा जास्त मशीन, ऑपरेटर ठेवतो' },
      { id: 'contractor_owner', text: 'कॉन्ट्रॅक्टर-मालक — स्वतःच्या मशीन आहेत आणि प्रकल्पही घेतो' },
    ],
  },
  machines_count: {
    label: 'तुमच्याकडे किती construction मशीन आहेत?',
    options: [
      { id: '1', text: '1' },
      { id: '2_3', text: '2–3' },
      { id: '4_5', text: '4–5' },
      { id: '6_plus', text: '6+' },
    ],
  },
  machine_types: {
    label: 'कोणत्या प्रकारच्या मशीन आहेत? (एकापेक्षा जास्त निवडा)',
    options: FOCUS_MACHINE_TYPE_OPTIONS_MR,
  },
  jcb_3dx_rental_rate: {
    label: 'JCB 3DX — तुमचा सध्याचा तासदर?',
    options: jcb3dxRateOptionsMr,
  },
  excavator_rental_rate: {
    label: 'Excavator / Poclain — तुमचा सध्याचा तासदर?',
    options: excavatorRateOptionsMr,
  },
  mini_excavator_rental_rate: {
    label: 'Mini Excavator — तुमचा सध्याचा तासदर?',
    options: miniExcavatorRateOptionsMr,
  },
  inf_fuel_rate: {
    label: 'सध्या डिझेल आणि टायरचे भाव एवढे वाढलेत, मग जुन्या दराने (उदा. ₹१०००/तास) गाडी चालवून तुमच्या हातात काही उरतंय का?',
    options: [
      { id: 'no_margin', text: 'अजिबात नाही, डिझेल आणि वरच्या खर्चातच सगळं जातंय, खिशात काहीच उरत नाही.' },
      { id: 'thin_margin', text: 'जेमतेम खर्च निघतोय, पण नफा नावालाच उरलाय.' },
      { id: 'rate_ok', text: 'होय, मला हा दर परवर्डतोय.' },
    ],
  },
  inf_parts_cost: {
    label: 'गेल्या 1–2 वर्षांत hydraulic oil, tyres, spare parts 30–40% महाग — वार्षिक नफ्यावर परिणाम?',
    options: [
      { id: 'doubled_cost', text: 'हो — maintenance दुप्पट, खिसा रिकामा' },
      { id: 'some_impact', text: 'थोडा फरक, पण manage करतो' },
      { id: 'no_change', text: 'नाही, खर्च पूर्वीइतकाच' },
    ],
  },
  inf_operator_salary: {
    label: 'महागाईमुळे ऑपरेटर पगार + भत्ता वाढ — आर्थिक ताण वाढतोय?',
    options: [
      { id: 'demand_hike', text: 'हो — जास्त पगार मागणी, नाहीतर सोडून जाण्याची धमकी' },
      { id: 'hike_no_work', text: 'पगार वाढवला, पण त्या प्रमाणात काम नाही' },
      { id: 'no_pressure', text: 'पगाराचा ताण नाही' },
    ],
  },
  inf_emi_blocked: {
    label: 'मार्केटमध्ये उधारीमुळं किंवा पेमेंट वेळेवर न आल्यानं, खिशातून किंवा व्याजानं पैसे काढून बँकेचा EMI भरायची वेळ येते का?',
    options: [
      { id: 'often_miss', text: 'होय, १ ते ३ महिने चकरा माराव्या लागत्यात, हप्त्याच्या तारखेला लय टेन्शन असतंय.' },
      { id: 'sometimes', text: 'काय-काय वेळा पैसं पूर्ण बुडत्यात, लय ॲडजस्टमेंट करावी लागत्ये.' },
      { id: 'emi_on_time', text: 'नाय, आपले गिऱ्हाईक फिक्स आहेत, पेमेंट वेळेवर पदरात पडतंय.' },
    ],
  },
  inf_idle_fuel: {
    label: 'ऑफ-सीझनमध्ये किंवा मंदीत काम न मिळाल्यामुळे तुमची गाडी कित्येक दिवस घरात किंवा गॅरेजला रिकामी उभी राहते का?',
    options: [
      { id: 'yes_thousands', text: 'होय, नियमित काम शोधायला लय कसरत करावी लागतीये, गाडी थांबली की तोटा होतोय.' },
      { id: 'suspect_no_track', text: 'महिना कसाबसा निघतोय, पण गाडीला पाहिजे तितकं नियमित काम मिळत नाही.' },
      { id: 'never_idle', text: 'नाय, आपले हक्काचे ग्राहक आहेत, गाडीला कंटिन्यू काम असतंय.' },
    ],
  },
  inf_wear_tear: {
    label: 'Tyre महाग — rough driving मुळे tyre + मशीन आयुष्य कमी — त्रास?',
    options: [
      { id: 'yes_tire_cost', text: 'हो — tyre लवकर खराब, मोठा भुर्दंड' },
      { id: 'minor_no_say', text: 'थोडे नुकसान, पण ऑपरेटरला बोलता येत नाही' },
      { id: 'no_issue', text: 'नाही, मशीन व्यवस्थित चालवली जाते' },
    ],
  },
  inf_diesel_theft: {
    label: 'मशीनवर डिझेल चोरी होणं किंवा काम नसताना ऑपरेटरनं विनाकारण गाडी चालू ठेवून डिझेल फुकट जाळण्याचा त्रास तुम्हाला बी होतोय का?',
    options: dieselTheftOptionsMr,
  },
  inf_payment_delay: {
    label: 'काम झाल्यानंतर भाड्याचे पैसे मिळायला सरासरी किती वेळ?',
    options: paymentDelayOptionsMr,
  },
  inf_side_booking: {
    label: 'ऑपरेटर तुम्हाला न सांगता परस्पर बाहेर खाजगी भाडी मारतोय किंवा मशीन रफ चालवून टायरचा वाटोळा करतोय, असा संशय कधी आलाय का?',
    options: sideBookingOptionsMr,
  },
  inf_manual_ledger: {
    label: 'रोजच्या कामाचे तास आणि डिझेलचा हिशोब डायरीत हाताने लिहिताना महिन्याच्या शेवटी ऑपरेटरसोबत \'कचकच\' किंवा हिशोबात घोळ होतो का?',
    options: manualLedgerOptionsMr,
  },
  inf_remote_lock: {
    label: 'मोबाईलवरून engine lock/unlock (Development Express) — फायदा?',
    options: remoteLockOptionsMr,
  },
  singlePain1: {
    label: 'EMI दबाव — client payment उशिरा, स्वतःच्या खिशातून EMI?',
    options: [
      { id: 'yes_every_month', text: 'हो — दर महिना संघर्ष' },
      { id: 'sometimes', text: 'कधी कधी' },
      { id: 'manageable', text: 'नाही — व्यवस्थापन होते' },
    ],
  },
  singlePain2: {
    label: 'पावसाळ्यात काम नाही — मशीन बंद, खर्च सुरू?',
    options: [
      { id: 'major_problem', text: 'मोठी समस्या' },
      { id: 'sometimes', text: 'कधी कधी' },
      { id: 'not_an_issue', text: 'समस्या नाही' },
    ],
  },
  singlePain3: {
    label: 'ऑपरेटरचे side booking — तास आणि डिझेल जुळत नाही?',
    options: [
      { id: 'happens_often', text: 'वारंवार होते' },
      { id: 'suspect_it', text: 'संशय आहे' },
      { id: 'operator_honest', text: 'नाही — ऑपरेटर प्रामाणिक' },
    ],
  },
  fleetPain1: {
    label: 'अनेक ऑपरेटर track करणे — मशीनमधून डिझेल चोरी?',
    options: [
      { id: 'major_problem', text: 'मोठी समस्या' },
      { id: 'sometimes', text: 'कधी कधी' },
      { id: 'not_an_issue', text: 'समस्या नाही' },
    ],
  },
  fleetPain2: {
    label: 'अनेक मशीनवर client payment विलंब — cash flow ताण?',
    options: [
      { id: 'happens_often', text: 'वारंवार होते' },
      { id: 'sometimes', text: 'कधी कधी' },
      { id: 'rarely', text: 'क्वचितच' },
    ],
  },
  fleetPain3: {
    label: '३+ मशीनसाठी manual ledger — महिना-अखेर reconciliation अशक्य?',
    options: [
      { id: 'yes_headache', text: 'हो — मोठी डोकेदुखी' },
      { id: 'sometimes_difficult', text: 'कधी कधी अवघड' },
      { id: 'manageable', text: 'व्यवस्थापन होते' },
    ],
  },
  contractorOwnerPain1: {
    label: 'स्वतःची मशीन स्वतःच्या प्रकल्पावर vs भाड्याने देणे — संघर्ष?',
    options: [
      { id: 'major_dilemma', text: 'मोठी दुविधा' },
      { id: 'sometimes', text: 'कधी कधी' },
      { id: 'not_an_issue', text: 'समस्या नाही' },
    ],
  },
  contractorOwnerPain2: {
    label: 'प्रकल्पाचे client payment उशिरा — EMI आणि operator salary दबाव?',
    options: [
      { id: 'happens_often', text: 'वारंवार होते' },
      { id: 'sometimes', text: 'कधी कधी' },
      { id: 'rarely', text: 'क्वचितच' },
    ],
  },
  contractorOwnerPain3: {
    label: 'स्वतःच्या प्रकल्पावर vs भाड्याने दिलेल्या मशीन तासांचा डिजिटल record नाही?',
    options: [
      { id: 'major_problem', text: 'मोठी समस्या' },
      { id: 'sometimes', text: 'कधी कधी' },
      { id: 'not_an_issue', text: 'समस्या नाही' },
    ],
  },
  uses_whatsapp: {
    label: 'तुम्ही WhatsApp रोज वापरता का?',
    options: whatsappOptionsMr,
  },
  trial_interest: {
    label: 'महागाईत fuel save + instant payment + digital control — Development Express 7 दिवस मोफत पाहाल?',
    options: [
      { id: 'yes_definitely', text: 'हो, डिजिटल उपाय हवा — नक्की पाहेन' },
      { id: 'maybe', text: 'आधी संपूर्ण माहिती, मग ठरवेन' },
      { id: 'no', text: 'नाही, पारंपारिक पद्धती' },
    ],
  },
};

const ownerQuestionsHi = {
  ownerType: {
    label: 'मशीन मालिक के रूप में आपका विवरण क्या है?',
    options: [
      { id: 'single', text: 'एकल मशीन मालिक — 1-2 मशीन, खुद चलाता हूँ' },
      { id: 'fleet', text: 'फ्लीट मालिक — 3 या अधिक मशीन, ऑपरेटर रखता हूँ' },
      { id: 'contractor_owner', text: 'कॉन्ट्रॅक्टर-मालिक — खुद की मशीन हैं और प्रोजेक्ट भी लेता हूँ' },
    ],
  },
  machines_count: {
    label: 'आपके पास कितनी construction मशीनें हैं?',
    options: [
      { id: '1', text: '1' },
      { id: '2_3', text: '2–3' },
      { id: '4_5', text: '4–5' },
      { id: '6_plus', text: '6+' },
    ],
  },
  machine_types: {
    label: 'किस प्रकार की मशीनें हैं? (एक से ज़्यादा चुन सकते हैं)',
    options: FOCUS_MACHINE_TYPE_OPTIONS_HI,
  },
  jcb_3dx_rental_rate: {
    label: 'JCB 3DX — आपका वर्तमान प्रति घंटा दर?',
    options: jcb3dxRateOptionsHi,
  },
  excavator_rental_rate: {
    label: 'Excavator / Poclain — आपका वर्तमान प्रति घंटा दर?',
    options: excavatorRateOptionsHi,
  },
  mini_excavator_rental_rate: {
    label: 'Mini Excavator — आपका वर्तमान प्रति घंटा दर?',
    options: miniExcavatorRateOptionsHi,
  },
  inf_fuel_rate: {
    label: 'महंगाई में ग्राहक से मिलने वाला प्रति घंटा दर सच में किफ़ायती है?',
    options: [
      { id: 'no_margin', text: 'नहीं — डीज़ल पर ही खर्च, हाथ में कुछ नहीं' },
      { id: 'thin_margin', text: 'बस-बस निकलता, मुनाफ़ा बहुत कम' },
      { id: 'rate_ok', text: 'हाँ, दर किफ़ायती है' },
    ],
  },
  inf_parts_cost: {
    label: 'पिछले 1–2 साल hydraulic oil, tyres, parts 30–40% महंगे — सालाना मुनाफ़े पर असर?',
    options: [
      { id: 'doubled_cost', text: 'हाँ — maintenance दोगुना, जेब खाली' },
      { id: 'some_impact', text: 'थोड़ा असर, manage कर लेते हैं' },
      { id: 'no_change', text: 'नहीं, खर्च पहले जैसा' },
    ],
  },
  inf_operator_salary: {
    label: 'महंगाई में ऑपरेटर वेतन + भत्ता बढ़ — आर्थिक दबाव?',
    options: [
      { id: 'demand_hike', text: 'हाँ — ज़्यादा वेतन, नहीं तो छोड़ने की धमकी' },
      { id: 'hike_no_work', text: 'वेतन बढ़ाया, उतना काम नहीं' },
      { id: 'no_pressure', text: 'वेतन का दबाव नहीं' },
    ],
  },
  inf_emi_blocked: {
    label: 'सब महंगा — ग्राहक से पैसे अटके, अपनी जेब/ब्याज से EMI?',
    options: [
      { id: 'often_miss', text: 'हाँ — EMI तारीख पर पैसे नहीं, वसूली नहीं' },
      { id: 'sometimes', text: 'कभी-कभी ऐसा होता है' },
      { id: 'emi_on_time', text: 'नहीं, EMI समय पर' },
    ],
  },
  inf_idle_fuel: {
    label: 'महंगा डीज़ल — site पर काम न हो तब idle engine — नुकसान?',
    options: [
      { id: 'yes_thousands', text: 'हाँ — महीने में हज़ारों रुपये बेकार' },
      { id: 'suspect_no_track', text: 'शक — ऑपरेटर पर नज़र नहीं' },
      { id: 'never_idle', text: 'मशीन खाली चालू नहीं रहती' },
    ],
  },
  inf_wear_tear: {
    label: 'Tyre महंगे — rough driving से tyre + machine life कम?',
    options: [
      { id: 'yes_tire_cost', text: 'हाँ — tyre जल्दी खराब, बड़ा खर्च' },
      { id: 'minor_no_say', text: 'थोड़ा नुकसान, बोल नहीं सकते' },
      { id: 'no_issue', text: 'नहीं, मशीन ठीक चलती है' },
    ],
  },
  inf_diesel_theft: {
    label: 'मशीन चलते समय डीज़ल चोरी या fuel हिसाब न मिलना?',
    options: dieselTheftOptionsHi,
  },
  inf_payment_delay: {
    label: 'काम के बाद किराया पैसा मिलने में औसतन कितना समय?',
    options: paymentDelayOptionsHi,
  },
  inf_side_booking: {
    label: 'ऑपरेटर side booking / private किराए — बिन बताए?',
    options: sideBookingOptionsHi,
  },
  inf_manual_ledger: {
    label: 'घंटे + डीज़ल manual diary — month-end घोल?',
    options: manualLedgerOptionsHi,
  },
  inf_remote_lock: {
    label: 'Mobile से engine lock/unlock (Development Express) — फायदा?',
    options: remoteLockOptionsHi,
  },
  singlePain1: {
    label: 'EMI दबाव — क्लाइंट भुगतान देरी, अपनी जेब से EMI?',
    options: [
      { id: 'yes_every_month', text: 'हाँ — हर महीने संघर्ष' },
      { id: 'sometimes', text: 'कभी-कभी' },
      { id: 'manageable', text: 'नहीं — प्रबंधनीय' },
    ],
  },
  singlePain2: {
    label: 'मानसून में काम नहीं — मशीन बंद, खर्च जारी?',
    options: [
      { id: 'major_problem', text: 'बड़ी समस्या' },
      { id: 'sometimes', text: 'कभी-कभी' },
      { id: 'not_an_issue', text: 'समस्या नहीं' },
    ],
  },
  singlePain3: {
    label: 'ऑपरेटर की साइड बुकिंग — घंटे और डीजल मेल नहीं?',
    options: [
      { id: 'happens_often', text: 'अक्सर होता है' },
      { id: 'suspect_it', text: 'संदेह है' },
      { id: 'operator_honest', text: 'नहीं — ऑपरेटर ईमानदार' },
    ],
  },
  fleetPain1: {
    label: 'कई ऑपरेटर ट्रैक करना — मशीनों से डीजल चोरी?',
    options: [
      { id: 'major_problem', text: 'बड़ी समस्या' },
      { id: 'sometimes', text: 'कभी-कभी' },
      { id: 'not_an_issue', text: 'समस्या नहीं' },
    ],
  },
  fleetPain2: {
    label: 'कई मशीनों पर क्लाइंट भुगतान देरी — नकदी प्रवाह तनाव?',
    options: [
      { id: 'happens_often', text: 'अक्सर होता है' },
      { id: 'sometimes', text: 'कभी-कभी' },
      { id: 'rarely', text: 'कभी नहीं' },
    ],
  },
  fleetPain3: {
    label: '3+ मशीनों के लिए manual ledger — महीने के अंत में reconciliation असंभव?',
    options: [
      { id: 'yes_headache', text: 'हाँ — बड़ी परेशानी' },
      { id: 'sometimes_difficult', text: 'कभी-कभी कठिन' },
      { id: 'manageable', text: 'प्रबंधनीय' },
    ],
  },
  contractorOwnerPain1: {
    label: 'खुद की मशीन खुद के प्रोजेक्ट पर vs किराए पर देना — संघर्ष?',
    options: [
      { id: 'major_dilemma', text: 'बड़ी दुविधा' },
      { id: 'sometimes', text: 'कभी-कभी' },
      { id: 'not_an_issue', text: 'समस्या नहीं' },
    ],
  },
  contractorOwnerPain2: {
    label: 'प्रोजेक्ट का क्लाइंट भुगतान देरी — EMI और ऑपरेटर वेतन दबाव?',
    options: [
      { id: 'happens_often', text: 'अक्सर होता है' },
      { id: 'sometimes', text: 'कभी-कभी' },
      { id: 'rarely', text: 'कभी नहीं' },
    ],
  },
  contractorOwnerPain3: {
    label: 'खुद के प्रोजेक्ट पर vs किराए पर दी मशीन के घंटों का डिजिटल रिकॉर्ड नहीं?',
    options: [
      { id: 'major_problem', text: 'बड़ी समस्या' },
      { id: 'sometimes', text: 'कभी-कभी' },
      { id: 'not_an_issue', text: 'समस्या नहीं' },
    ],
  },
  uses_whatsapp: {
    label: 'क्या आप WhatsApp रोज़ इस्तेमाल करते हैं?',
    options: whatsappOptionsHi,
  },
  trial_interest: {
    label: 'महंगाई में fuel save + instant payment + digital control — Development Express 7 दिन free?',
    options: [
      { id: 'yes_definitely', text: 'हाँ, digital solution चाहिए — ज़रूर' },
      { id: 'maybe', text: 'पहले पूरी जानकारी' },
      { id: 'no', text: 'नहीं, पारंपरिक तरीका' },
    ],
  },
};

const ownerQuestionsEn = {
  ownerType: {
    label: 'Which best describes you as a machine owner?',
    options: [
      { id: 'single', text: 'Single Machine Owner — 1 or 2 machines, operate myself' },
      { id: 'fleet', text: 'Fleet Owner — 3 or more machines, hire operators' },
      { id: 'contractor_owner', text: 'Contractor-Owner — own machines and take projects' },
    ],
  },
  machines_count: {
    label: 'How many construction machines do you own?',
    options: [
      { id: '1', text: '1' },
      { id: '2_3', text: '2–3' },
      { id: '4_5', text: '4–5' },
      { id: '6_plus', text: '6+' },
    ],
  },
  machine_types: {
    label: 'What type of machines? (select all that apply)',
    options: FOCUS_MACHINE_TYPE_OPTIONS_EN,
  },
  jcb_3dx_rental_rate: {
    label: 'JCB 3DX — your current hourly rate?',
    options: jcb3dxRateOptionsEn,
  },
  excavator_rental_rate: {
    label: 'Excavator / Poclain — your current hourly rate?',
    options: excavatorRateOptionsEn,
  },
  mini_excavator_rental_rate: {
    label: 'Mini Excavator — your current hourly rate?',
    options: miniExcavatorRateOptionsEn,
  },
  inf_fuel_rate: {
    label: 'With inflation, does the hourly rate you get from clients really work?',
    options: [
      { id: 'no_margin', text: 'No — diesel eats it all, nothing left' },
      { id: 'thin_margin', text: 'Breaks even, profit very thin' },
      { id: 'rate_ok', text: 'Yes — rate is workable' },
    ],
  },
  inf_parts_cost: {
    label: 'Hydraulic oil, tyres, parts up 30–40% in 1–2 years — hit on annual profit?',
    options: [
      { id: 'doubled_cost', text: 'Yes — maintenance doubled, pocket empty' },
      { id: 'some_impact', text: 'Some impact, managing' },
      { id: 'no_change', text: 'No — costs about the same' },
    ],
  },
  inf_operator_salary: {
    label: 'Inflation — operator salary + daily allowance hikes — financial stress?',
    options: [
      { id: 'demand_hike', text: 'Yes — demands hike or threatens to quit' },
      { id: 'hike_no_work', text: 'Raised pay, not enough work to match' },
      { id: 'no_pressure', text: 'No salary pressure' },
    ],
  },
  inf_emi_blocked: {
    label: 'Everything costly — client dues stuck, paying EMI from your pocket/loan?',
    options: [
      { id: 'often_miss', text: 'Yes — EMI date, no cash, dues not collected' },
      { id: 'sometimes', text: 'Sometimes happens' },
      { id: 'emi_on_time', text: 'No — EMIs on time' },
    ],
  },
  inf_idle_fuel: {
    label: 'Costly diesel — idle engine on site with no work — fuel loss?',
    options: [
      { id: 'yes_thousands', text: 'Yes — thousands wasted monthly' },
      { id: 'suspect_no_track', text: 'Suspect it — cannot watch operator' },
      { id: 'never_idle', text: 'Machine never runs idle' },
    ],
  },
  inf_wear_tear: {
    label: 'Tyres costly — rough driving shortens tyre & machine life?',
    options: [
      { id: 'yes_tire_cost', text: 'Yes — tyres fail early, big bill' },
      { id: 'minor_no_say', text: 'Some damage, cannot confront operator' },
      { id: 'no_issue', text: 'No — machine driven properly' },
    ],
  },
  inf_diesel_theft: {
    label: 'Diesel theft or no accurate fuel accounting while machine runs?',
    options: dieselTheftOptionsEn,
  },
  inf_payment_delay: {
    label: 'After work, how long until rental payment on average?',
    options: paymentDelayOptionsEn,
  },
  inf_side_booking: {
    label: 'Operator side booking / private hire without telling you?',
    options: sideBookingOptionsEn,
  },
  inf_manual_ledger: {
    label: 'Hours + diesel manual diary — month-end confusion?',
    options: manualLedgerOptionsEn,
  },
  inf_remote_lock: {
    label: 'Mobile engine lock/unlock (Development Express) — would it help?',
    options: remoteLockOptionsEn,
  },
  singlePain1: {
    label: 'EMI pressure — client payment delayed, paying EMI from own pocket?',
    options: [
      { id: 'yes_every_month', text: 'Yes — every month struggle' },
      { id: 'sometimes', text: 'Sometimes' },
      { id: 'manageable', text: 'No — manageable' },
    ],
  },
  singlePain2: {
    label: 'No work during monsoon — machine idle, fixed costs continue?',
    options: [
      { id: 'major_problem', text: 'Major problem' },
      { id: 'sometimes', text: 'Sometimes' },
      { id: 'not_an_issue', text: 'Not an issue' },
    ],
  },
  singlePain3: {
    label: 'Operator side booking — hours and diesel not matching?',
    options: [
      { id: 'happens_often', text: 'Happens often' },
      { id: 'suspect_it', text: 'Suspect it' },
      { id: 'operator_honest', text: 'No — operator honest' },
    ],
  },
  fleetPain1: {
    label: 'Tracking multiple operators — diesel theft across machines?',
    options: [
      { id: 'major_problem', text: 'Major problem' },
      { id: 'sometimes', text: 'Sometimes' },
      { id: 'not_an_issue', text: 'Not an issue' },
    ],
  },
  fleetPain2: {
    label: 'Client payment delay across multiple machines — cash flow stress?',
    options: [
      { id: 'happens_often', text: 'Happens often' },
      { id: 'sometimes', text: 'Sometimes' },
      { id: 'rarely', text: 'Rarely' },
    ],
  },
  fleetPain3: {
    label: 'Manual ledger for 3+ machines — month-end reconciliation impossible?',
    options: [
      { id: 'yes_headache', text: 'Yes — major headache' },
      { id: 'sometimes_difficult', text: 'Sometimes difficult' },
      { id: 'manageable', text: 'Manageable' },
    ],
  },
  contractorOwnerPain1: {
    label: 'Using own machine on own project vs renting out — conflict?',
    options: [
      { id: 'major_dilemma', text: 'Major dilemma' },
      { id: 'sometimes', text: 'Sometimes' },
      { id: 'not_an_issue', text: 'Not an issue' },
    ],
  },
  contractorOwnerPain2: {
    label: 'Client payment for project delayed — EMI and operator salary pressure?',
    options: [
      { id: 'happens_often', text: 'Happens often' },
      { id: 'sometimes', text: 'Sometimes' },
      { id: 'rarely', text: 'Rarely' },
    ],
  },
  contractorOwnerPain3: {
    label: 'No digital record of machine hours used on own project vs hired out?',
    options: [
      { id: 'major_problem', text: 'Major problem' },
      { id: 'sometimes', text: 'Sometimes' },
      { id: 'not_an_issue', text: 'Not an issue' },
    ],
  },
  uses_whatsapp: {
    label: 'Do you use WhatsApp daily?',
    options: whatsappOptionsEn,
  },
  trial_interest: {
    label: 'Inflation era — fuel save + instant pay + digital control — try Development Express free 7 days?',
    options: [
      { id: 'yes_definitely', text: 'Yes — need digital fix, definitely try' },
      { id: 'maybe', text: 'Need full info first' },
      { id: 'no', text: 'No — traditional way' },
    ],
  },
};

const clientQuestionsMr = {
  clientProfession: {
    label: 'तुमचे काम कशाशी संबंधित आहे?',
    options: [
      { id: 'farmer', text: 'शेती' },
      { id: 'road', text: 'रस्ते व बांधकाम' },
      { id: 'industrial', text: 'औद्योगिक' },
      { id: 'government', text: 'सरकारी/PWD' },
      { id: 'builder', text: 'रिअल इस्टेट' },
      { id: 'well', text: 'विहीर खोदणे' },
      { id: 'other', text: 'इतर' },
    ],
  },
  hire_frequency: {
    label: 'किती वेळा construction मशीन भाड्याने घ्यावी लागते?',
    options: [
      { id: 'weekly', text: 'आठवड्यातून 1+ वेळ' },
      { id: 'monthly', text: 'महिन्यात 1–2 वेळ' },
      { id: 'quarterly', text: '3–4 महिन्यातून एकदा' },
      { id: 'rarely', text: 'फार क्वचित / पहिल्यांदाच' },
    ],
  },
  occupation: {
    label: 'तुमचे काम काय आहे?',
    options: [
      { id: 'road_contractor', text: 'Road / Infrastructure Contractor' },
      { id: 'housing_developer', text: 'Housing / Plot Developer' },
      { id: 'farmer', text: 'शेतकरी / शेत काम' },
      { id: 'other_work', text: 'इतर काम' },
    ],
  },
  machine_types_needed: {
    label: 'सर्वात जास्त कोणती मशीन हवी असते?',
    options: FOCUS_MACHINE_TYPE_OPTIONS_MR,
  },
  jcb_3dx_pay_rate: {
    label: 'JCB 3DX — तासाला साधारण किती भाडे देता?',
    options: jcb3dxRateOptionsMr,
  },
  excavator_pay_rate: {
    label: 'Excavator / Poclain — तासाला साधारण किती भाडे देता?',
    options: excavatorRateOptionsMr,
  },
  mini_excavator_pay_rate: {
    label: 'Mini Excavator — तासाला साधारण किती भाडे देता?',
    options: miniExcavatorRateOptionsMr,
  },
  inf_budget_creep: {
    label: 'सिमेंट-स्टील-खडी महाग + मशीन unexpected bill — project budget हाताबाहेर?',
    options: [
      { id: 'yes_margin_gone', text: 'हो — profit margin संपतो' },
      { id: 'slight_manage', text: 'थोडे वाढते, manage करावे लागते' },
      { id: 'controlled', text: 'नाही, budget नियंत्रणात' },
    ],
  },
  inf_fuel_surcharge: {
    label: 'काम संपल्यावर प्रवासाचं भाडं किंवा ऑपरेटरचा चहा-पाणी / भत्ता यावरून मालकासोबत सुरुवातीला ठरलेले रेट बदलून नाहक वाद होतात का?',
    options: [
      { id: 'yes_mid_work', text: 'होय, शेवटी एक्स्ट्रा पैशांची मागणी केली जाते, काम अडकू नये म्हणून वाद टाळावे लागतात.' },
      { id: 'travel_disputes', text: 'कधीमधी प्रवासाच्या खर्चावरून किरकोळ कटकट होतीये.' },
      { id: 'fixed_rate', text: 'नाय, जे सुरुवातीला ठरतं, त्याच दराप्रमाणे फायनल बिल होतंय.' },
    ],
  },
  inf_rounding_loss: {
    label: 'जेसीबी किंवा पोकलेन भाड्यानं चालवताना, ऑपरेटरनं प्रत्यक्ष कामापेक्षा डायरीत \'तास\' जास्त लावून किंवा ४ तास १५ मिनिटांचे थेट ५ तास लिहून बिलात काटा मारल्याचा अनुभव आलाय का?',
    options: [
      { id: 'yes_thousands', text: 'होय, काम कमी होतंय आणि मॅन्युअल बिलामध्ये हजारो रुपयांचा चुना लावला जातोय.' },
      { id: 'suspect_no_record', text: 'शंका येतीये, पण साईटवर प्रत्येक मिनिट मोजायला माणूस ठेवणं परवडत नाय.' },
      { id: 'accurate', text: 'नाय, आमचे तास आणि बिल नेहमी ठरल्याप्रमाणे अचूक असत्यात.' },
    ],
  },
  inf_cash_crunch: {
    label: 'मंदी + महागाई — bills उशीर, मालकाला full cash देणे कठीण?',
    options: [
      { id: 'yes_working_capital', text: 'हो — working capital अडकले, full payment जमत नाही' },
      { id: 'sometimes_delay', text: 'कधीकधी थोडे दिवस थांबण्याची विनंती' },
      { id: 'cash_ok', text: 'नाही, लगेच cash देतो' },
    ],
  },
  inf_slow_progress: {
    label: 'जुनी/खराब मशीन — काम slow — site वर मजूराचा रोजचा पगार waste?',
    options: [
      { id: 'yes_double_loss', text: 'हो — मजूर waste + double loss' },
      { id: 'sometimes', text: 'कधीकधी खोळंबा' },
      { id: 'high_speed', text: 'नाही, high-speed मशीन मिळते' },
    ],
  },
  inf_punctuality: {
    label: 'ऐन नडीच्या वेळी मशीन वेळेवर न आल्यामुळं किंवा साईटवर आल्या आल्या अचानक बंद पडल्यामुळं, तुमचे मजूर फुकट बसून प्रोजेक्टचं बजेट कोलमडलंय का?',
    options: punctualityOptionsMr,
  },
  inf_calls: {
    label: 'योग्य दरात मशीन — किती call + घासाघीस?',
    options: callsOptionsMr,
  },
  inf_reliability: {
    label: 'करार झाल्यावर जास्त दर मिळाला की booking cancel?',
    options: reliabilityOptionsMr,
  },
  inf_advance_trust: {
    label: 'नवीन किंवा अनोळखी मशीन मालकाला सुरुवातीला ॲडव्हान्स दिल्यावर, त्यानं ऐनवेळी गाडी नाही पाठवली तर पैसे बुडण्याची भीती वाटते का?',
    options: [
      { id: 'yes_afraid', text: 'होय, मार्केटमध्ये असले प्रकार लय घडत्यात, पैसं अडकल्यावर सारखा फोन लावावा लागतोय.' },
      { id: 'known_only', text: 'म्हणूनच आम्ही अनोळखी लोकांना सोडून फक्त ओळखीच्या गाड्या भाड्यानं घेतोय.' },
      { id: 'no_fear', text: 'नाय, आपल्याला अशी काही भीती वाटत नाही.' },
    ],
  },
  inf_digital_ledger: {
    label: 'महागाईच्या काळात, तुमच्या सगळ्या साईट्सवर मशीनवर एकूण किती खर्च झालाय, याचा पक्का हिशोब एका क्लिकवर मोबाईलमध्ये बघायला आवडेल का?',
    options: [
      { id: 'yes_audit_easy', text: 'होय, यामुळे महिन्याला खर्च कुठं जास्त होतोय हे ट्रॅक करणं आणि हिशोब तपासणं लय सोपं पडेल.' },
      { id: 'maybe_diary_ok', text: 'बघायला आवडेल, पण सध्या डायरीवर बी चालू शकतंय.' },
      { id: 'no_need', text: 'गरज वाटत नाही.' },
    ],
  },
  farmerPain1: {
    label: 'मशीन वेळेवर न येणे — मजूर आणि हंगाम वाया?',
    options: [
      { id: 'happens_often', text: 'वारंवार होते' },
      { id: 'occasionally', text: 'कधी कधी' },
      { id: 'rarely', text: 'क्वचितच' },
    ],
  },
  farmerPain2: {
    label: 'अनोळखी मालकाला अग्रिम देण्याची भीती?',
    options: [
      { id: 'always_worried', text: 'नेहमी काळजी वाटते' },
      { id: 'sometimes', text: 'कधी कधी' },
      { id: 'not_an_issue', text: 'समस्या नाही' },
    ],
  },
  farmerPain3: {
    label: 'पीक हंगामात मशीन उपलब्ध नाही?',
    options: [
      { id: 'major_problem', text: 'मोठी समस्या' },
      { id: 'sometimes', text: 'कधी कधी' },
      { id: 'not_an_issue', text: 'समस्या नाही' },
    ],
  },
  roadPain1: {
    label: 'कामाच्या मध्ये अनपेक्षित दरवाढ किंवा डिझेल चार्ज?',
    options: [
      { id: 'happens_often', text: 'वारंवार होते' },
      { id: 'occasionally', text: 'कधी कधी' },
      { id: 'rarely', text: 'क्वचितच' },
    ],
  },
  roadPain2: {
    label: 'मालक GST invoice देत नाही?',
    options: [
      { id: 'never_gets', text: 'कधीच मिळत नाही' },
      { id: 'sometimes_gets', text: 'कधी कधी मिळते' },
      { id: 'always_gets', text: 'नेहमी मिळते' },
    ],
  },
  roadPain3: {
    label: 'ऑपरेटरचे कौशल्य साइटच्या गरजेशी जुळत नाही?',
    options: [
      { id: 'major_problem', text: 'मोठी समस्या' },
      { id: 'sometimes', text: 'कधी कधी' },
      { id: 'not_an_issue', text: 'समस्या नाही' },
    ],
  },
  industrialPain1: {
    label: 'मालकाकडून कोणताही करार किंवा कागदपत्र नाही?',
    options: [
      { id: 'never_gets', text: 'कधीच मिळत नाही' },
      { id: 'sometimes', text: 'कधी कधी' },
      { id: 'always_gets', text: 'नेहमी मिळते' },
    ],
  },
  industrialPain2: {
    label: 'मशीन विश्वासार्हता — कामाच्या मध्ये बिघाड?',
    options: [
      { id: 'happens_often', text: 'वारंवार होते' },
      { id: 'occasionally', text: 'कधी कधी' },
      { id: 'rarely', text: 'क्वचितच' },
    ],
  },
  industrialPain3: {
    label: 'दीर्घकालीन मशीन करार अटींमध्ये अडचण?',
    options: [
      { id: 'major_problem', text: 'मोठी समस्या' },
      { id: 'sometimes', text: 'कधी कधी' },
      { id: 'not_an_issue', text: 'समस्या नाही' },
    ],
  },
  govtPain1: {
    label: 'मालक वर्क ऑर्डरसाठी compliance कागदपत्रे देऊ शकत नाही?',
    options: [
      { id: 'major_problem', text: 'मोठी समस्या' },
      { id: 'sometimes', text: 'कधी कधी' },
      { id: 'not_an_issue', text: 'समस्या नाही' },
    ],
  },
  govtPain2: {
    label: 'विभागाकडून payment विलंब — मालक अग्रिमासाठी दबाव?',
    options: [
      { id: 'happens_often', text: 'वारंवार होते' },
      { id: 'occasionally', text: 'कधी कधी' },
      { id: 'rarely', text: 'क्वचितच' },
    ],
  },
  govtPain3: {
    label: 'GST invoice आणि e-way bill मिळत नाही?',
    options: [
      { id: 'never_gets', text: 'कधीच मिळत नाही' },
      { id: 'sometimes', text: 'कधी कधी' },
      { id: 'always_gets', text: 'नेहमी मिळते' },
    ],
  },
  builderPain1: {
    label: 'साइटवर अनेक मशीन समन्वय — वेळापत्रक समस्या?',
    options: [
      { id: 'major_problem', text: 'मोठी समस्या' },
      { id: 'sometimes', text: 'कधी कधी' },
      { id: 'not_an_issue', text: 'समस्या नाही' },
    ],
  },
  builderPain2: {
    label: 'मालकाचे न येणे किंवा शेवटच्या क्षणी रद्द?',
    options: [
      { id: 'happens_often', text: 'वारंवार होते' },
      { id: 'occasionally', text: 'कधी कधी' },
      { id: 'rarely', text: 'क्वचितच' },
    ],
  },
  builderPain3: {
    label: 'मशीन तास आणि बिलिंगचा डिजिटल रेकॉर्ड नाही?',
    options: [
      { id: 'major_problem', text: 'मोठी समस्या' },
      { id: 'sometimes', text: 'कधी कधी' },
      { id: 'not_an_issue', text: 'समस्या नाही' },
    ],
  },
  wellPain1: {
    label: 'विहीर खोदणारा विशेष ऑपरेटर उपलब्ध नाही?',
    options: [
      { id: 'major_problem', text: 'मोठी समस्या' },
      { id: 'sometimes', text: 'कधी कधी' },
      { id: 'not_an_issue', text: 'समस्या नाही' },
    ],
  },
  wellPain2: {
    label: 'दुर्गम/अवघड साइटवर मशीन आणणे?',
    options: [
      { id: 'major_problem', text: 'मोठी समस्या' },
      { id: 'sometimes', text: 'कधी कधी' },
      { id: 'not_an_issue', text: 'समस्या नाही' },
    ],
  },
  wellPain3: {
    label: 'मालक १-३ दिवसांच्या छोट्या भाड्यासाठी तयार नाही?',
    options: [
      { id: 'happens_often', text: 'वारंवार होते' },
      { id: 'occasionally', text: 'कधी कधी' },
      { id: 'rarely', text: 'क्वचितच' },
    ],
  },
  uses_whatsapp: {
    label: 'तुम्ही WhatsApp रोज वापरता का?',
    options: whatsappOptionsMr,
  },
  trial_interest: {
    label: 'जर एकाच मोबाईल ॲपवरून जेसीबी, पोकलेन आणि मिनी पोकलेन या मशिन्स एका क्लिकवर आणि फिक्स (स्टँडर्ड) दरात बुक करता आल्या, तर तुम्ही ते ॲप वापरायला बघणार का?',
    options: [
      { id: 'yes_definitely', text: 'नक्कीच! वेगवेगळ्या मालकांना फोन लावून दरासाठी घासाघीस करण्यापेक्षा हे लय सोपं होईल.' },
      { id: 'maybe', text: 'रेट आणि सर्व्हिस कशी मिळतीये त्यावर ठरवू.' },
      { id: 'no', text: 'नाय, आपले जुने ओळखीचे लोक पुरेसे आहेत.' },
    ],
  },
};

const clientQuestionsHi = {
  clientProfession: {
    label: 'आपका काम किससे संबंधित है?',
    options: [
      { id: 'farmer', text: 'कृषि' },
      { id: 'road', text: 'सड़क और निर्माण' },
      { id: 'industrial', text: 'औद्योगिक' },
      { id: 'government', text: 'सरकारी/PWD' },
      { id: 'builder', text: 'रियल एस्टेट' },
      { id: 'well', text: 'कुआं खोदना' },
      { id: 'other', text: 'अन्य' },
    ],
  },
  hire_frequency: {
    label: 'कितनी बार construction मशीन किराए पर लेनी पड़ती है?',
    options: [
      { id: 'weekly', text: 'हफ्ते में 1+ बार' },
      { id: 'monthly', text: 'महीने में 1–2 बार' },
      { id: 'quarterly', text: '3–4 महीने में एक बार' },
      { id: 'rarely', text: 'बहुत कम / पहली बार' },
    ],
  },
  occupation: {
    label: 'आपका काम क्या है?',
    options: [
      { id: 'road_contractor', text: 'Road / Infrastructure Contractor' },
      { id: 'housing_developer', text: 'Housing / Plot Developer' },
      { id: 'farmer', text: 'किसान / खेत काम' },
      { id: 'other_work', text: 'अन्य काम' },
    ],
  },
  machine_types_needed: {
    label: 'सबसे ज़्यादा किस मशीन की ज़रूरत पड़ती है?',
    options: FOCUS_MACHINE_TYPE_OPTIONS_HI,
  },
  jcb_3dx_pay_rate: {
    label: 'JCB 3DX — प्रति घंटा आमतौर पर कितना किराया देते हैं?',
    options: jcb3dxRateOptionsHi,
  },
  excavator_pay_rate: {
    label: 'Excavator / Poclain — प्रति घंटा आमतौर पर कितना किराया देते हैं?',
    options: excavatorRateOptionsHi,
  },
  mini_excavator_pay_rate: {
    label: 'Mini Excavator — प्रति घंटा आमतौर पर कितना किराया देते हैं?',
    options: miniExcavatorRateOptionsHi,
  },
  inf_budget_creep: {
    label: 'सीमेंट-स्टील-गिट्टी महंगी + मशीन unexpected bill — project budget बाहर?',
    options: [
      { id: 'yes_margin_gone', text: 'हाँ — profit margin खत्म' },
      { id: 'slight_manage', text: 'थोड़ा बढ़ता, manage करते हैं' },
      { id: 'controlled', text: 'नहीं, budget control में' },
    ],
  },
  inf_fuel_surcharge: {
    label: 'महंगाई का बहाना — बीच काम में rate बढ़ / extra diesel?',
    options: [
      { id: 'yes_mid_work', text: 'हाँ — काम रोककर ज़्यादा पैसे' },
      { id: 'travel_disputes', text: 'कभी-कभी traveling charges विवाद' },
      { id: 'fixed_rate', text: 'नहीं, तय दर पर' },
    ],
  },
  inf_rounding_loss: {
    label: '4 घंटे 15 मिनट → diary में 5 घंटे — bill में golmaal?',
    options: [
      { id: 'yes_thousands', text: 'हाँ — 15–30 मिनट extra, हज़ारों रुपये' },
      { id: 'suspect_no_record', text: 'शक — supervisor के पास exact record नहीं' },
      { id: 'accurate', text: 'नहीं, हिसाब सही' },
    ],
  },
  inf_cash_crunch: {
    label: 'मंदी + महंगाई — bills late, owner को full cash मुश्किल?',
    options: [
      { id: 'yes_working_capital', text: 'हाँ — working capital अटका, full pay नहीं' },
      { id: 'sometimes_delay', text: 'कभी-कभी कुछ दिन रुकने की बिनती' },
      { id: 'cash_ok', text: 'नहीं, तुरंत cash' },
    ],
  },
  inf_slow_progress: {
    label: 'पुरानी/खराब मशीन — slow काम — site पर मजदूर daily waste?',
    options: [
      { id: 'yes_double_loss', text: 'हाँ — मजदूर waste + double loss' },
      { id: 'sometimes', text: 'कभी-कभी रुकावट' },
      { id: 'high_speed', text: 'नहीं, fast machine मिलती है' },
    ],
  },
  inf_punctuality: {
    label: 'Book के बाद machine समय पर site न पहुँचे — काम रुके?',
    options: punctualityOptionsHi,
  },
  inf_calls: {
    label: 'सही rate पर machine — kitne call + घसीट?',
    options: callsOptionsHi,
  },
  inf_reliability: {
    label: 'Deal के बाद ज़्यादा rate मिला तो booking cancel?',
    options: reliabilityOptionsHi,
  },
  inf_advance_trust: {
    label: 'अनजान owner को advance — machine न आए तो डर?',
    options: [
      { id: 'yes_afraid', text: 'हाँ — escrow/commitment' },
      { id: 'known_only', text: 'सिर्फ known contacts' },
      { id: 'no_fear', text: 'डर नहीं' },
    ],
  },
  inf_live_map: {
    label: 'Book machine live map (Zomato/Uber)?',
    options: liveMapOptionsHi,
  },
  inf_digital_ledger: {
    label: 'महंगाई में हर रुपये का हिसाब — सभी machine monthly digital report mobile पर?',
    options: [
      { id: 'yes_audit_easy', text: 'ज़रूर — audit + cost track आसान' },
      { id: 'maybe_diary_ok', text: 'अच्छा लगेगा, diary भी चलती है' },
      { id: 'no_need', text: 'ज़रूरत नहीं' },
    ],
  },
  farmerPain1: {
    label: 'मशीन समय पर न आना — मजदूर और मौसम बर्बाद?',
    options: [
      { id: 'happens_often', text: 'अक्सर होता है' },
      { id: 'occasionally', text: 'कभी-कभी' },
      { id: 'rarely', text: 'कभी नहीं' },
    ],
  },
  farmerPain2: {
    label: 'अनजान मालिक को अग्रिम देने का डर?',
    options: [
      { id: 'always_worried', text: 'हमेशा चिंता' },
      { id: 'sometimes', text: 'कभी-कभी' },
      { id: 'not_an_issue', text: 'समस्या नहीं' },
    ],
  },
  farmerPain3: {
    label: 'फसल के मौसम में मशीन उपलब्ध नहीं?',
    options: [
      { id: 'major_problem', text: 'बड़ी समस्या' },
      { id: 'sometimes', text: 'कभी-कभी' },
      { id: 'not_an_issue', text: 'समस्या नहीं' },
    ],
  },
  roadPain1: {
    label: 'काम के बीच अचानक दर वृद्धि या डीजल चार्ज?',
    options: [
      { id: 'happens_often', text: 'अक्सर होता है' },
      { id: 'occasionally', text: 'कभी-कभी' },
      { id: 'rarely', text: 'कभी नहीं' },
    ],
  },
  roadPain2: {
    label: 'मालिक GST invoice नहीं देता?',
    options: [
      { id: 'never_gets', text: 'कभी नहीं मिलता' },
      { id: 'sometimes_gets', text: 'कभी-कभी मिलता' },
      { id: 'always_gets', text: 'हमेशा मिलता' },
    ],
  },
  roadPain3: {
    label: 'ऑपरेटर कौशल साइट की जरूरत से मेल नहीं?',
    options: [
      { id: 'major_problem', text: 'बड़ी समस्या' },
      { id: 'sometimes', text: 'कभी-कभी' },
      { id: 'not_an_issue', text: 'समस्या नहीं' },
    ],
  },
  industrialPain1: {
    label: 'मालिक से कोई अनुबंध या दस्तावेज नहीं?',
    options: [
      { id: 'never_gets', text: 'कभी नहीं मिलता' },
      { id: 'sometimes', text: 'कभी-कभी' },
      { id: 'always_gets', text: 'हमेशा मिलता' },
    ],
  },
  industrialPain2: {
    label: 'मशीन विश्वसनीयता — काम के बीच खराबी?',
    options: [
      { id: 'happens_often', text: 'अक्सर होता है' },
      { id: 'occasionally', text: 'कभी-कभी' },
      { id: 'rarely', text: 'कभी नहीं' },
    ],
  },
  industrialPain3: {
    label: 'दीर्घकालीन मशीन अनुबंध शर्तों में कठिनाई?',
    options: [
      { id: 'major_problem', text: 'बड़ी समस्या' },
      { id: 'sometimes', text: 'कभी-कभी' },
      { id: 'not_an_issue', text: 'समस्या नहीं' },
    ],
  },
  govtPain1: {
    label: 'मालिक वर्क ऑर्डर के लिए compliance दस्तावेज नहीं दे सकता?',
    options: [
      { id: 'major_problem', text: 'बड़ी समस्या' },
      { id: 'sometimes', text: 'कभी-कभी' },
      { id: 'not_an_issue', text: 'समस्या नहीं' },
    ],
  },
  govtPain2: {
    label: 'विभाग से भुगतान में देरी — मालिक अग्रिम का दबाव?',
    options: [
      { id: 'happens_often', text: 'अक्सर होता है' },
      { id: 'occasionally', text: 'कभी-कभी' },
      { id: 'rarely', text: 'कभी नहीं' },
    ],
  },
  govtPain3: {
    label: 'GST invoice और e-way bill नहीं मिलता?',
    options: [
      { id: 'never_gets', text: 'कभी नहीं मिलता' },
      { id: 'sometimes', text: 'कभी-कभी' },
      { id: 'always_gets', text: 'हमेशा मिलता' },
    ],
  },
  builderPain1: {
    label: 'साइट पर कई मशीनों का समन्वय — शेड्यूल समस्या?',
    options: [
      { id: 'major_problem', text: 'बड़ी समस्या' },
      { id: 'sometimes', text: 'कभी-कभी' },
      { id: 'not_an_issue', text: 'समस्या नहीं' },
    ],
  },
  builderPain2: {
    label: 'मालिक का न आना या अंतिम समय पर रद्द?',
    options: [
      { id: 'happens_often', text: 'अक्सर होता है' },
      { id: 'occasionally', text: 'कभी-कभी' },
      { id: 'rarely', text: 'कभी नहीं' },
    ],
  },
  builderPain3: {
    label: 'मशीन घंटे और बिलिंग का कोई डिजिटल रिकॉर्ड नहीं?',
    options: [
      { id: 'major_problem', text: 'बड़ी समस्या' },
      { id: 'sometimes', text: 'कभी-कभी' },
      { id: 'not_an_issue', text: 'समस्या नहीं' },
    ],
  },
  wellPain1: {
    label: 'कुआं खोदने का विशेष ऑपरेटर उपलब्ध नहीं?',
    options: [
      { id: 'major_problem', text: 'बड़ी समस्या' },
      { id: 'sometimes', text: 'कभी-कभी' },
      { id: 'not_an_issue', text: 'समस्या नहीं' },
    ],
  },
  wellPain2: {
    label: 'दूरदराज/कठिन साइट पर मशीन पहुंचाना?',
    options: [
      { id: 'major_problem', text: 'बड़ी समस्या' },
      { id: 'sometimes', text: 'कभी-कभी' },
      { id: 'not_an_issue', text: 'समस्या नहीं' },
    ],
  },
  wellPain3: {
    label: 'मालिक 1-3 दिन की छोटी हायरिंग के लिए तैयार नहीं?',
    options: [
      { id: 'happens_often', text: 'अक्सर होता है' },
      { id: 'occasionally', text: 'कभी-कभी' },
      { id: 'rarely', text: 'कभी नहीं' },
    ],
  },
  uses_whatsapp: {
    label: 'क्या आप WhatsApp रोज़ इस्तेमाल करते हैं?',
    options: whatsappOptionsHi,
  },
  trial_interest: {
    label: 'Fixed transparent rates + 50% instant pay + digital billing — Development Express app?',
    options: [
      { id: 'yes_definitely', text: 'ज़रूर — budget control + fair rates' },
      { id: 'maybe', text: 'rate और service देखकर' },
      { id: 'no', text: 'नहीं, पुराने contact काफी' },
    ],
  },
};

const clientQuestionsEn = {
  clientProfession: {
    label: 'What best describes your work?',
    options: [
      { id: 'farmer', text: 'Farmer / Agriculture' },
      { id: 'road', text: 'Road and Civil Construction' },
      { id: 'industrial', text: 'Industrial and Factory' },
      { id: 'government', text: 'Government / PWD' },
      { id: 'builder', text: 'Real Estate / Builder' },
      { id: 'well', text: 'Well Digging' },
      { id: 'other', text: 'Other' },
    ],
  },
  hire_frequency: {
    label: 'How often do you hire construction machines?',
    options: [
      { id: 'weekly', text: '1+ times per week' },
      { id: 'monthly', text: '1–2 times per month' },
      { id: 'quarterly', text: 'Once every 3–4 months' },
      { id: 'rarely', text: 'Very rarely / first time' },
    ],
  },
  occupation: {
    label: 'What is your line of work?',
    options: [
      { id: 'road_contractor', text: 'Road / Infrastructure Contractor' },
      { id: 'housing_developer', text: 'Housing / Plot Developer' },
      { id: 'farmer', text: 'Farmer / Agricultural work' },
      { id: 'other_work', text: 'Other work' },
    ],
  },
  machine_types_needed: {
    label: 'Which machine do you need most often?',
    options: FOCUS_MACHINE_TYPE_OPTIONS_EN,
  },
  jcb_3dx_pay_rate: {
    label: 'JCB 3DX — what do you usually pay per hour?',
    options: jcb3dxRateOptionsEn,
  },
  excavator_pay_rate: {
    label: 'Excavator / Poclain — what do you usually pay per hour?',
    options: excavatorRateOptionsEn,
  },
  mini_excavator_pay_rate: {
    label: 'Mini Excavator — what do you usually pay per hour?',
    options: miniExcavatorRateOptionsEn,
  },
  inf_budget_creep: {
    label: 'Cement-steel-aggregate up + surprise machine bills — project budget bust?',
    options: [
      { id: 'yes_margin_gone', text: 'Yes — profit margin wiped out' },
      { id: 'slight_manage', text: 'Budget grows a bit, we manage' },
      { id: 'controlled', text: 'No — budget under control' },
    ],
  },
  inf_fuel_surcharge: {
    label: 'Inflation excuse — mid-job rate hike or extra diesel charges?',
    options: [
      { id: 'yes_mid_work', text: 'Yes — work held up for more money' },
      { id: 'travel_disputes', text: 'Sometimes travel charge disputes' },
      { id: 'fixed_rate', text: 'No — agreed rate holds' },
    ],
  },
  inf_rounding_loss: {
    label: '4 hr 15 min logged as 5 hrs in manual diary — billing fraud?',
    options: [
      { id: 'yes_thousands', text: 'Yes — 15–30 min padded, costs thousands' },
      { id: 'suspect_no_record', text: 'Suspect it — no exact supervisor record' },
      { id: 'accurate', text: 'No — accounts are accurate' },
    ],
  },
  inf_cash_crunch: {
    label: 'Slow market + inflation — late bills, hard to pay owner full cash?',
    options: [
      { id: 'yes_working_capital', text: 'Yes — working capital stuck, cannot pay full' },
      { id: 'sometimes_delay', text: 'Sometimes ask for few days delay' },
      { id: 'cash_ok', text: 'No — we pay cash promptly' },
    ],
  },
  inf_slow_progress: {
    label: 'Old/breakdown machine — slow work — daily labour wages wasted on site?',
    options: [
      { id: 'yes_double_loss', text: 'Yes — labour waste + double loss' },
      { id: 'sometimes', text: 'Sometimes stalls' },
      { id: 'high_speed', text: 'No — get fast machines' },
    ],
  },
  inf_punctuality: {
    label: 'Booked machine not on time at site — labour/work stalled?',
    options: punctualityOptionsEn,
  },
  inf_calls: {
    label: 'Fair-rate machine — how many calls + negotiation?',
    options: callsOptionsEn,
  },
  inf_reliability: {
    label: 'Booking cancelled after deal when owner got higher rate?',
    options: reliabilityOptionsEn,
  },
  inf_advance_trust: {
    label: 'Advance to unknown owner — fear if machine does not come?',
    options: [
      { id: 'yes_afraid', text: 'Yes — need escrow/commitment' },
      { id: 'known_only', text: 'Advance to known only' },
      { id: 'no_fear', text: 'No fear' },
    ],
  },
  inf_live_map: {
    label: 'See booked machine on live map (like Zomato/Uber)?',
    options: liveMapOptionsEn,
  },
  inf_digital_ledger: {
    label: 'Every rupee counts in inflation — monthly digital report for all machines on phone?',
    options: [
      { id: 'yes_audit_easy', text: 'Definitely — audit & cost tracking easy' },
      { id: 'maybe_diary_ok', text: 'Would like it, diary works for now' },
      { id: 'no_need', text: 'No need' },
    ],
  },
  farmerPain1: {
    label: 'Machine not arriving on time — labour and season wasted?',
    options: [
      { id: 'happens_often', text: 'Happens often' },
      { id: 'occasionally', text: 'Occasionally' },
      { id: 'rarely', text: 'Rarely' },
    ],
  },
  farmerPain2: {
    label: 'Fear of paying advance to unknown owner?',
    options: [
      { id: 'always_worried', text: 'Always worried' },
      { id: 'sometimes', text: 'Sometimes' },
      { id: 'not_an_issue', text: 'Not an issue' },
    ],
  },
  farmerPain3: {
    label: 'Machine unavailable during peak season?',
    options: [
      { id: 'major_problem', text: 'Major problem' },
      { id: 'sometimes', text: 'Sometimes' },
      { id: 'not_an_issue', text: 'Not an issue' },
    ],
  },
  roadPain1: {
    label: 'Unexpected rate hike or diesel surcharge mid-job?',
    options: [
      { id: 'happens_often', text: 'Happens often' },
      { id: 'occasionally', text: 'Occasionally' },
      { id: 'rarely', text: 'Rarely' },
    ],
  },
  roadPain2: {
    label: 'Owner does not provide GST invoice?',
    options: [
      { id: 'never_gets', text: 'Never gets invoice' },
      { id: 'sometimes_gets', text: 'Sometimes gets' },
      { id: 'always_gets', text: 'Always gets' },
    ],
  },
  roadPain3: {
    label: 'Operator skill not matching site requirements?',
    options: [
      { id: 'major_problem', text: 'Major problem' },
      { id: 'sometimes', text: 'Sometimes' },
      { id: 'not_an_issue', text: 'Not an issue' },
    ],
  },
  industrialPain1: {
    label: 'No written contract or documentation from owner?',
    options: [
      { id: 'never_gets', text: 'Never gets docs' },
      { id: 'sometimes', text: 'Sometimes' },
      { id: 'always_gets', text: 'Always gets' },
    ],
  },
  industrialPain2: {
    label: 'Machine reliability — frequent breakdowns mid-job?',
    options: [
      { id: 'happens_often', text: 'Happens often' },
      { id: 'occasionally', text: 'Occasionally' },
      { id: 'rarely', text: 'Rarely' },
    ],
  },
  industrialPain3: {
    label: 'Difficulty in long-term machine contract terms?',
    options: [
      { id: 'major_problem', text: 'Major problem' },
      { id: 'sometimes', text: 'Sometimes' },
      { id: 'not_an_issue', text: 'Not an issue' },
    ],
  },
  govtPain1: {
    label: 'Owner unable to provide compliance documents for work order?',
    options: [
      { id: 'major_problem', text: 'Major problem' },
      { id: 'sometimes', text: 'Sometimes' },
      { id: 'not_an_issue', text: 'Not an issue' },
    ],
  },
  govtPain2: {
    label: 'Payment delay from department — owner pressures for advance?',
    options: [
      { id: 'happens_often', text: 'Happens often' },
      { id: 'occasionally', text: 'Occasionally' },
      { id: 'rarely', text: 'Rarely' },
    ],
  },
  govtPain3: {
    label: 'GST invoice and e-way bill not provided?',
    options: [
      { id: 'never_gets', text: 'Never gets' },
      { id: 'sometimes', text: 'Sometimes' },
      { id: 'always_gets', text: 'Always gets' },
    ],
  },
  builderPain1: {
    label: 'Coordinating multiple machines on site — scheduling issues?',
    options: [
      { id: 'major_problem', text: 'Major problem' },
      { id: 'sometimes', text: 'Sometimes' },
      { id: 'not_an_issue', text: 'Not an issue' },
    ],
  },
  builderPain2: {
    label: 'Owner no-show or last-minute cancellation?',
    options: [
      { id: 'happens_often', text: 'Happens often' },
      { id: 'occasionally', text: 'Occasionally' },
      { id: 'rarely', text: 'Rarely' },
    ],
  },
  builderPain3: {
    label: 'No digital record of machine hours and billing?',
    options: [
      { id: 'major_problem', text: 'Major problem' },
      { id: 'sometimes', text: 'Sometimes' },
      { id: 'not_an_issue', text: 'Not an issue' },
    ],
  },
  wellPain1: {
    label: 'Specialized well-digging operator not available?',
    options: [
      { id: 'major_problem', text: 'Major problem' },
      { id: 'sometimes', text: 'Sometimes' },
      { id: 'not_an_issue', text: 'Not an issue' },
    ],
  },
  wellPain2: {
    label: 'Machine mobilization to remote/difficult site?',
    options: [
      { id: 'major_problem', text: 'Major problem' },
      { id: 'sometimes', text: 'Sometimes' },
      { id: 'not_an_issue', text: 'Not an issue' },
    ],
  },
  wellPain3: {
    label: 'Owner unwilling for short 1-3 day hire?',
    options: [
      { id: 'happens_often', text: 'Happens often' },
      { id: 'occasionally', text: 'Occasionally' },
      { id: 'rarely', text: 'Rarely' },
    ],
  },
  uses_whatsapp: {
    label: 'Do you use WhatsApp daily?',
    options: whatsappOptionsEn,
  },
  trial_interest: {
    label: 'Fixed transparent rates + 50% instant pay + digital billing — Development Express app?',
    options: [
      { id: 'yes_definitely', text: 'Yes — budget control + fair rates' },
      { id: 'maybe', text: 'Will decide after rates & service' },
      { id: 'no', text: 'No — old contacts enough' },
    ],
  },
};

/** ST2 — English. ST3 — Marathi + Hindi below. */
const pmQuestionsEn = {
  pmProjectsCount: {
    label: 'How many projects do you manage simultaneously?',
    options: [
      { id: 'one', text: '1 project' },
      { id: 'two_three', text: '2–3 projects' },
      { id: 'four_six', text: '4–6 projects' },
      { id: 'seven_plus', text: '7 or more' },
    ],
  },
  pmSourceMethod: {
    label: 'How do you currently source equipment?',
    options: [
      { id: 'through_broker', text: 'Through broker' },
      { id: 'known_owner', text: 'Known owner/contact' },
      { id: 'online_marketplace', text: 'Online marketplace' },
      { id: 'company_owned_fleet', text: 'Company-owned fleet' },
    ],
  },
  pmBiggestChallenge: {
    label: 'Biggest challenge when hiring equipment?',
    options: [
      { id: 'reliability_punctuality', text: 'Reliability and punctuality' },
      { id: 'gst_documentation', text: 'GST invoice and documentation' },
      { id: 'rate_disputes', text: 'Rate disputes mid-project' },
      { id: 'equipment_availability', text: 'Equipment availability' },
    ],
  },
  pmBudgetRange: {
    label: 'Average equipment hire budget per month?',
    options: [
      { id: 'under_1l', text: 'Under 1 Lakh' },
      { id: '1_5l', text: '1–5 Lakh' },
      { id: '5_10l', text: '5–10 Lakh' },
      { id: 'above_10l', text: 'Above 10 Lakh' },
    ],
  },
  pmDecisionAuthority: {
    label: 'Who approves equipment hiring decisions?',
    options: [
      { id: 'self', text: 'I decide independently' },
      { id: 'project_director', text: 'Project director approval' },
      { id: 'procurement', text: 'Company procurement team' },
      { id: 'client_approval', text: 'Client approval required' },
    ],
  },
  uses_whatsapp: {
    label: 'Do you use WhatsApp daily?',
    options: whatsappOptionsEn,
  },
};

const contractorQuestionsEn = {
  contractorProjectType: {
    label: 'Type of projects you handle?',
    options: [
      { id: 'road_highway', text: 'Road and highway' },
      { id: 'housing_residential', text: 'Housing and residential' },
      { id: 'excavation_earthwork', text: 'Excavation and earthwork' },
      { id: 'industrial_commercial', text: 'Industrial and commercial' },
    ],
  },
  contractorHireFrequency: {
    label: 'How often do you hire equipment?',
    options: [
      { id: 'weekly', text: 'Weekly' },
      { id: 'monthly', text: 'Monthly' },
      { id: 'per_project', text: 'Per project basis' },
      { id: 'seasonally', text: 'Seasonally' },
    ],
  },
  contractorPain: {
    label: 'Biggest pain with current equipment suppliers?',
    options: [
      { id: 'large_advance', text: 'Large advance payment required' },
      { id: 'owner_noshow', text: 'Owner no-show or cancellation' },
      { id: 'rate_hike', text: 'Unexpected rate hike mid-job' },
      { id: 'no_documentation', text: 'No documentation or receipt' },
    ],
  },
  contractorGst: {
    label: 'Do you require GST invoices?',
    options: [
      { id: 'always', text: 'Always — company requirement' },
      { id: 'sometimes', text: 'Sometimes' },
      { id: 'not_required', text: 'Not required' },
    ],
  },
  contractorPayment: {
    label: 'Preferred payment method?',
    options: [
      { id: 'digital_wallet', text: 'Digital wallet' },
      { id: 'bank_transfer', text: 'Bank transfer' },
      { id: 'cheque', text: 'Cheque' },
      { id: 'cash', text: 'Cash' },
    ],
  },
  uses_whatsapp: {
    label: 'Do you use WhatsApp daily?',
    options: whatsappOptionsEn,
  },
};

/** ST3 — Marathi + Hindi (same option ids as EN). */
const pmQuestionsMr = {
  pmProjectsCount: {
    label: 'तुम्ही एकाच वेळी किती प्रकल्प सांभाळता?',
    options: [
      { id: 'one', text: '१ प्रकल्प' },
      { id: 'two_three', text: '२–३ प्रकल्प' },
      { id: 'four_six', text: '४–६ प्रकल्प' },
      { id: 'seven_plus', text: '७ पेक्षा जास्त' },
    ],
  },
  pmSourceMethod: {
    label: 'सध्या मशीन कुठून मिळवता?',
    options: [
      { id: 'through_broker', text: 'दलालामार्फत' },
      { id: 'known_owner', text: 'ओळखीचा मालक/संपर्क' },
      { id: 'online_marketplace', text: 'ऑनलाइन मार्केटप्लेस' },
      { id: 'company_owned_fleet', text: 'कंपनीची स्वतःची मशीन' },
    ],
  },
  pmBiggestChallenge: {
    label: 'मशीन भाड्याने घेताना सर्वात मोठी अडचण?',
    options: [
      { id: 'reliability_punctuality', text: 'विश्वासार्हता आणि वेळेवर येणे' },
      { id: 'gst_documentation', text: 'GST इन्व्हॉइस आणि कागदपत्रे' },
      { id: 'rate_disputes', text: 'प्रकल्पाच्या मध्ये दर वाढवणे' },
      { id: 'equipment_availability', text: 'मशीन उपलब्धता' },
    ],
  },
  pmBudgetRange: {
    label: 'दरमहा सरासरी मशीन भाडे खर्च?',
    options: [
      { id: 'under_1l', text: '१ लाखाखाली' },
      { id: '1_5l', text: '१–५ लाख' },
      { id: '5_10l', text: '५–१० लाख' },
      { id: 'above_10l', text: '१० लाखांवर' },
    ],
  },
  pmDecisionAuthority: {
    label: 'मशीन भाड्याचा निर्णय कोण घेतो?',
    options: [
      { id: 'self', text: 'मी स्वतः निर्णय घेतो' },
      { id: 'project_director', text: 'प्रकल्प संचालकाची मंजुरी' },
      { id: 'procurement', text: 'कंपनीची खरेदी टीम' },
      { id: 'client_approval', text: 'क्लायंटची मंजुरी आवश्यक' },
    ],
  },
  uses_whatsapp: {
    label: 'तुम्ही रोज WhatsApp वापरता का?',
    options: whatsappOptionsMr,
  },
};

const contractorQuestionsMr = {
  contractorProjectType: {
    label: 'तुम्ही कोणत्या प्रकारचे प्रकल्प हाताळता?',
    options: [
      { id: 'road_highway', text: 'रस्ते आणि महामार्ग' },
      { id: 'housing_residential', text: 'घरे आणि निवासी' },
      { id: 'excavation_earthwork', text: 'उत्खनन आणि मातीकाम' },
      { id: 'industrial_commercial', text: 'औद्योगिक आणि व्यावसायिक' },
    ],
  },
  contractorHireFrequency: {
    label: 'तुम्ही किती वेळा मशीन भाड्याने घेता?',
    options: [
      { id: 'weekly', text: 'आठवड्याला' },
      { id: 'monthly', text: 'महिन्याला' },
      { id: 'per_project', text: 'प्रकल्पानुसार' },
      { id: 'seasonally', text: 'हंगामानुसार' },
    ],
  },
  contractorPain: {
    label: 'सध्याच्या मशीन पुरवठादारांसोबत सर्वात मोठी समस्या?',
    options: [
      { id: 'large_advance', text: 'मोठी अग्रिम रक्कम लागते' },
      { id: 'owner_noshow', text: 'मालकाचे न येणे किंवा रद्द करणे' },
      { id: 'rate_hike', text: 'कामाच्या मध्ये अनपेक्षित दरवाढ' },
      { id: 'no_documentation', text: 'कोणतेही कागदपत्र किंवा पावती नाही' },
    ],
  },
  contractorGst: {
    label: 'GST इन्व्हॉइस आवश्यक आहे का?',
    options: [
      { id: 'always', text: 'नेहमी — कंपनीची आवश्यकता' },
      { id: 'sometimes', text: 'कधीकधी' },
      { id: 'not_required', text: 'आवश्यक नाही' },
    ],
  },
  contractorPayment: {
    label: 'पसंतीची पेमेंट पद्धत?',
    options: [
      { id: 'digital_wallet', text: 'डिजिटल वॉलेट' },
      { id: 'bank_transfer', text: 'बँक ट्रान्सफर' },
      { id: 'cheque', text: 'चेक' },
      { id: 'cash', text: 'रोख' },
    ],
  },
  uses_whatsapp: {
    label: 'तुम्ही रोज WhatsApp वापरता का?',
    options: whatsappOptionsMr,
  },
};

const pmQuestionsHi = {
  pmProjectsCount: {
    label: 'आप एक साथ कितने प्रोजेक्ट संभालते हैं?',
    options: [
      { id: 'one', text: '1 प्रोजेक्ट' },
      { id: 'two_three', text: '2–3 प्रोजेक्ट' },
      { id: 'four_six', text: '4–6 प्रोजेक्ट' },
      { id: 'seven_plus', text: '7 से अधिक' },
    ],
  },
  pmSourceMethod: {
    label: 'वर्तमान में मशीन कहाँ से लेते हैं?',
    options: [
      { id: 'through_broker', text: 'दलाल के माध्यम से' },
      { id: 'known_owner', text: 'जाने-पहचाने मालक/संपर्क' },
      { id: 'online_marketplace', text: 'ऑनलाइन मार्केटप्लेस' },
      { id: 'company_owned_fleet', text: 'कंपनी की खुद की मशीन' },
    ],
  },
  pmBiggestChallenge: {
    label: 'मशीन किराए पर लेने में सबसे बड़ी चुनौती?',
    options: [
      { id: 'reliability_punctuality', text: 'विश्वसनीयता और समय पर आना' },
      { id: 'gst_documentation', text: 'GST इनवॉइस और दस्तावेज़' },
      { id: 'rate_disputes', text: 'प्रोजेक्ट के बीच दर बढ़ाना' },
      { id: 'equipment_availability', text: 'मशीन की उपलब्धता' },
    ],
  },
  pmBudgetRange: {
    label: 'मासिक औसत मशीन किराया खर्च?',
    options: [
      { id: 'under_1l', text: '1 लाख से कम' },
      { id: '1_5l', text: '1–5 लाख' },
      { id: '5_10l', text: '5–10 लाख' },
      { id: 'above_10l', text: '10 लाख से अधिक' },
    ],
  },
  pmDecisionAuthority: {
    label: 'मशीन किराए का निर्णय कौन लेता है?',
    options: [
      { id: 'self', text: 'मैं स्वयं निर्णय लेता हूँ' },
      { id: 'project_director', text: 'प्रोजेक्ट डायरेक्टर की मंजूरी' },
      { id: 'procurement', text: 'कंपनी की खरीद टीम' },
      { id: 'client_approval', text: 'क्लाइंट की मंजूरी आवश्यक' },
    ],
  },
  uses_whatsapp: {
    label: 'क्या आप रोज़ WhatsApp इस्तेमाल करते/करती हैं?',
    options: whatsappOptionsHi,
  },
};

const contractorQuestionsHi = {
  contractorProjectType: {
    label: 'आप किस प्रकार के प्रोजेक्ट संभालते हैं?',
    options: [
      { id: 'road_highway', text: 'सड़क और राजमार्ग' },
      { id: 'housing_residential', text: 'आवास और आवासीय' },
      { id: 'excavation_earthwork', text: 'उत्खनन और मिट्टी का काम' },
      { id: 'industrial_commercial', text: 'औद्योगिक और व्यावसायिक' },
    ],
  },
  contractorHireFrequency: {
    label: 'आप कितनी बार मशीन किराए पर लेते हैं?',
    options: [
      { id: 'weekly', text: 'साप्ताहिक' },
      { id: 'monthly', text: 'मासिक' },
      { id: 'per_project', text: 'प्रोजेक्ट के अनुसार' },
      { id: 'seasonally', text: 'मौसमी' },
    ],
  },
  contractorPain: {
    label: 'वर्तमान मशीन आपूर्तिकर्ताओं के साथ सबसे बड़ी समस्या?',
    options: [
      { id: 'large_advance', text: 'बड़ी अग्रिम राशि आवश्यक' },
      { id: 'owner_noshow', text: 'मालिक का न आना या रद्द करना' },
      { id: 'rate_hike', text: 'काम के बीच अप्रत्याशित दर वृद्धि' },
      { id: 'no_documentation', text: 'कोई दस्तावेज़ या रसीद नहीं' },
    ],
  },
  contractorGst: {
    label: 'क्या GST इनवॉइस आवश्यक है?',
    options: [
      { id: 'always', text: 'हमेशा — कंपनी की आवश्यकता' },
      { id: 'sometimes', text: 'कभी-कभी' },
      { id: 'not_required', text: 'आवश्यक नहीं' },
    ],
  },
  contractorPayment: {
    label: 'पसंदीदा भुगतान विधि?',
    options: [
      { id: 'digital_wallet', text: 'डिजिटल वॉलेट' },
      { id: 'bank_transfer', text: 'बैंक ट्रांसफर' },
      { id: 'cheque', text: 'चेक' },
      { id: 'cash', text: 'नकद' },
    ],
  },
  uses_whatsapp: {
    label: 'क्या आप रोज़ WhatsApp इस्तेमाल करते/करती हैं?',
    options: whatsappOptionsHi,
  },
};

export const surveyTranslations = {
  mr: {
    langPills: { mr: 'मराठी', hi: 'हिंदी', en: 'English' },
    brand: 'Development Express',
    titles: {
      owner: 'मशीन मालक सर्वे',
      client: 'ग्राहक सर्वे',
      pm: 'प्रोजेक्ट मॅनेजर सर्वे',
      contractor: 'स्वतंत्र कॉन्ट्रॅक्टर सर्वे',
    },
    subtitle: 'Development Express — महागाई सर्वे (3–4 मिनिट)',
    sections: {
      contact: 'संपर्क माहिती',
      questions: 'प्रश्न',
    },
    fields: {
      name: 'पूर्ण नाव (PAN card प्रमाणे)',
      namePlaceholder: 'उदा. राम कृष्ण पाटील',
      nameHint: 'नाव + आडनाव — PAN card सारखे, अंक नको.',
      phone: 'WhatsApp / मोबाइल (10 अंक)',
      phonePlaceholder: '9876543210',
      phoneHint: 'वैध 10 अंकी नंबर (6–9 ने सुरू) — अपूर्ण नंबरवर पुढे जाणार नाही.',
      village: 'गाव / परिसर',
      villagePlaceholder: 'उदा. वाघोली, पुणे',
    },
    consent: 'मी सहमत आहे की माझी माहिती Development Express कडे संपर्कासाठी वापरली जाईल.',
    buttons: {
      submit: 'सर्वे सबमिट करा',
      submitting: 'पाठवत आहे…',
    },
    closingPitch: {
      owner: {
        headline: 'कष्टाची मशीन, आता कमावणार पारदर्शक नफा!',
        body: 'Development Express च्या Hassle-Free सिस्टीमसोबत डिझेल चोरी आणि उधारीला लावा पूर्णविराम. करा Smart Work, वाढवा Smart Earning आणि बना आत्मनिर्भर यशस्वी उद्योजक.',
        tagline: 'प्रगतीचा नवा एक्सप्रेस!',
        cta: 'आमच्याशी जोडा',
        ctaHint: 'WhatsApp वर instant संपर्क',
        waMessage: 'नमस्कार Development Express! मी *मशीन मालक सर्वे* पूर्ण केला.\nनाव: {name}\nWhatsApp: {phone}\nगाव: {village}\nHassle-Free system बद्दल अधिक माहिती हवी. कृपया संपर्क साधा.',
      },
      client: {
        headline: 'वेळेची बचत आणि बिलांची पूर्ण पारदर्शकता!',
        body: 'Development Express च्या Hassle-Free सिस्टीमसोबत site वर प्रत्येक मिनिटाचा हिशोब ठेवा सुरक्षित. करा Smart Work, वाचवा आपले भांडवल आणि बना प्रगत उद्योजक.',
        tagline: 'तुमची site, आमची वचनबद्धता!',
        cta: 'आमच्याशी जोडा',
        ctaHint: 'WhatsApp वर instant संपर्क',
        waMessage: 'नमस्कार Development Express! मी *ग्राहक/contractor सर्वे* पूर्ण केला.\nनाव: {name}\nWhatsApp: {phone}\nगाव: {village}\nTransparent billing + site tracking बद्दल अधिक माहिती हवी.',
      },
    },
    errors: {
      nameRequired: 'कृपया पूर्ण नाव भरा.',
      nameInvalid: 'PAN card प्रमाणे पूर्ण नाव (नाव + आडनाव) — अंक नको.',
      phoneRequired: 'कृपया 10 अंकी WhatsApp नंबर भरा.',
      phoneInvalid: '10 अंकी वैध मोबाइल नंबर भरा (6–9 ने सुरू).',
      villageRequired: 'कृपया गाव / परिसर भरा.',
      consentRequired: 'कृपया सहमती checkbox लावा.',
      answerRequired: 'कृपया हा प्रश्न उत्तर द्या.',
      ownerTypeRequired: 'पुढे जाण्यासाठी तुमचा मालक प्रकार निवडा',
      clientProfessionRequired: 'पुढे जाण्यासाठी तुमचा व्यवसाय प्रकार निवडा',
      duplicate: 'हा नंबर या सर्वेसाठी आधीच भरला आहे.',
      duplicateSurveyed: 'हा नंबर आधीच सर्वे केला आहे.',
      blockedReferral: 'Invalid or Blocked Referral Code — admin शी संपर्क करा.',
      rateLimit: '24 तासात जास्तीत जास्त 2 सर्वे — कृपया नंतर पुन्हा प्रयत्न करा.',
      generic: 'सबमिट अयशस्वी. कृपया पुन्हा प्रयत्न करा.',
      invalidSurvey: 'अवैध सर्वे प्रकार.',
    },
    thankYou: {
      title: 'धन्यवाद!',
      body: 'तुमचे उत्तर मिळाले. लवकरच आमची टीम संपर्क करेल.',
      hotLead: 'तुम्ही Development Express वापरण्यास उत्सुक आहात — आम्ही लवकरच कॉल करू!',
    },
    offlineSaved: 'Network नाही — फॉर्म save झाला, network आल्यावर आपोआप पाठवला जाईल ✓',
    pendingSync: '⏳ {count} pending sync',
    questions: {
      owner: ownerQuestionsMr,
      client: clientQuestionsMr,
      pm: pmQuestionsMr,
      contractor: contractorQuestionsMr,
    },
  },
  hi: {
    langPills: { mr: 'मराठी', hi: 'हिंदी', en: 'English' },
    brand: 'Development Express',
    titles: {
      owner: 'मशीन मालिक सर्वे',
      client: 'ग्राहक सर्वे',
      pm: 'प्रोजेक्ट मैनेजर सर्वे',
      contractor: 'स्वतंत्र कॉन्ट्रैक्टर सर्वे',
    },
    subtitle: 'Development Express — महंगाई सर्वे (3–4 मिनट)',
    sections: {
      contact: 'संपर्क जानकारी',
      questions: 'प्रश्न',
    },
    fields: {
      name: 'पूरा नाम (PAN card जैसा)',
      namePlaceholder: 'जैसे राम कृष्ण पाटिल',
      nameHint: 'नाम + उपनाम — PAN card जैसा, अंक नहीं।',
      phone: 'WhatsApp / मोबाइल (10 अंक)',
      phonePlaceholder: '9876543210',
      phoneHint: 'वैध 10 अंक — 6–9 से शुरू — अधूरा नंबर पर आगे नहीं।',
      village: 'गाँव / इलाका',
      villagePlaceholder: 'जैसे वाघोली, पुणे',
    },
    consent: 'मैं सहमत हूँ कि मेरी जानकारी Development Express द्वारा संपर्क के लिए उपयोग की जाएगी।',
    buttons: {
      submit: 'सर्वे सबमिट करें',
      submitting: 'भेज रहे हैं…',
    },
    closingPitch: {
      owner: {
        headline: 'मेहनत की मशीन, अब कमाएगी पारदर्शी मुनाफ़ा!',
        body: 'Development Express Hassle-Free system — डीज़ल चोरी और udhaari par full stop. Smart Work करें, Smart Earning बढ़ाएँ, आत्मनिर्भर उद्यमी बनें.',
        tagline: 'प्रगति का नया Express!',
        cta: 'हमसे जुड़ें',
        ctaHint: 'WhatsApp पर instant contact',
        waMessage: 'नमस्ते Development Express! मैंने *machine owner survey* पूरा किया।\nनाम: {name}\nWhatsApp: {phone}\nइलाका: {village}\nHassle-Free system की जानकारी चाहिए।',
      },
      client: {
        headline: 'समय की बचत + Complete billing transparency!',
        body: 'Development Express Hassle-Free system — site पर हर minute का safe हिसाब. Smart Work, capital बचाएँ, प्रगत उद्यमी बनें.',
        tagline: 'आपकी site, हमारी commitment.',
        cta: 'हमसे जुड़ें',
        ctaHint: 'WhatsApp पर instant contact',
        waMessage: 'नमस्ते Development Express! मैंने *client/contractor survey* पूरा किया।\nनाम: {name}\nWhatsApp: {phone}\nइलाका: {village}\nTransparent billing की जानकारी चाहिए।',
      },
    },
    errors: {
      nameRequired: 'कृपया पूरा नाम भरें।',
      nameInvalid: 'PAN card जैसा पूरा नाम (नाम + उपनाम) — अंक नहीं।',
      phoneRequired: 'कृपया 10 अंक का WhatsApp नंबर भरें।',
      phoneInvalid: '10 अंकों का वैध मोबाइल नंबर भरें (6–9 से शुरू)।',
      villageRequired: 'कृपया गाँव / इलाका भरें।',
      consentRequired: 'कृपया सहमति checkbox लगाएँ।',
      answerRequired: 'कृपया इस प्रश्न का उत्तर दें।',
      ownerTypeRequired: 'जारी रखने के लिए अपना मालिक प्रकार चुनें',
      clientProfessionRequired: 'जारी रखने के लिए अपना पेशा चुनें',
      duplicate: 'यह नंबर इस सर्वे के लिए पहले से भरा गया है।',
      duplicateSurveyed: 'This number has already been surveyed.',
      blockedReferral: 'Invalid or Blocked Referral Code — admin से संपर्क करें।',
      rateLimit: '24 घंटे में अधिकतम 2 सर्वे — कृपया बाद में पुनः प्रयास करें।',
      generic: 'सबमिट विफल। कृपया पुनः प्रयास करें।',
      invalidSurvey: 'अमान्य सर्वे प्रकार।',
    },
    thankYou: {
      title: 'धन्यवाद!',
      body: 'आपका उत्तर मिल गया। हमारी टीम जल्द संपर्क करेगी।',
      hotLead: 'आप Development Express उपयोग करने के इच्छुक हैं — हम जल्द कॉल करेंगे!',
    },
    offlineSaved: 'Network नहीं — फॉर्म save हो गया, network आने पर अपने आप भेजा जाएगा ✓',
    pendingSync: '⏳ {count} pending sync',
    questions: {
      owner: ownerQuestionsHi,
      client: clientQuestionsHi,
      pm: pmQuestionsHi,
      contractor: contractorQuestionsHi,
    },
  },
  en: {
    langPills: { mr: 'मराठी', hi: 'हिंदी', en: 'English' },
    brand: 'Development Express',
    titles: {
      owner: 'Machine Owner Survey',
      client: 'Client Survey',
      pm: 'Project Manager Survey',
      contractor: 'Independent Contractor Survey',
    },
    subtitle: 'Development Express — Inflation survey (3–4 minutes)',
    sections: {
      contact: 'Contact details',
      questions: 'Questions',
    },
    fields: {
      name: 'Full name (as on PAN card)',
      namePlaceholder: 'e.g. Ram Krishna Patil',
      nameHint: 'First + last name — as on PAN card, no numbers.',
      phone: 'WhatsApp / mobile (10 digits)',
      phonePlaceholder: '9876543210',
      phoneHint: 'Valid 10-digit number starting 6–9 — cannot proceed without it.',
      village: 'Village / area',
      villagePlaceholder: 'e.g. Wagholi, Pune',
    },
    consent: 'I agree that my information may be used by Development Express for contact purposes.',
    buttons: {
      submit: 'Submit survey',
      submitting: 'Sending…',
    },
    closingPitch: {
      owner: {
        headline: 'Hard work machine — now transparent profit!',
        body: 'With Development Express Hassle-Free system — stop diesel theft & endless dues. Smart Work, Smart Earning, become a self-reliant entrepreneur.',
        tagline: 'The new Express of progress!',
        cta: 'Join the Revolution',
        ctaHint: 'Instant contact on WhatsApp',
        waMessage: 'Hello Development Express! I completed the *machine owner survey*.\nName: {name}\nWhatsApp: {phone}\nArea: {village}\nI want to know more about the Hassle-Free system.',
      },
      client: {
        headline: 'Save time + Complete billing transparency!',
        body: 'With Development Express Hassle-Free system — track every site minute safely. Smart Work, save capital, grow as a modern contractor.',
        tagline: 'Your site, our commitment.',
        cta: 'Join the Revolution',
        ctaHint: 'Instant contact on WhatsApp',
        waMessage: 'Hello Development Express! I completed the *client/contractor survey*.\nName: {name}\nWhatsApp: {phone}\nArea: {village}\nI want transparent billing & site tracking.',
      },
    },
    errors: {
      nameRequired: 'Please enter your full name.',
      nameInvalid: 'Enter full name as on PAN card (first + last name, no numbers).',
      phoneRequired: 'Please enter a 10-digit WhatsApp number.',
      phoneInvalid: 'Enter a valid 10-digit mobile number (starting 6–9).',
      villageRequired: 'Please enter village / area.',
      consentRequired: 'Please check the consent box.',
      answerRequired: 'Please answer this question.',
      ownerTypeRequired: 'Please select your owner type to continue',
      clientProfessionRequired: 'Please select your profession to continue',
      duplicate: 'This number has already submitted this survey.',
      duplicateSurveyed: 'This number has already been surveyed.',
      blockedReferral: 'Invalid or Blocked Referral Code — contact admin.',
      rateLimit: 'Maximum 2 surveys per 24 hours — please try again later.',
      generic: 'Submit failed. Please try again.',
      invalidSurvey: 'Invalid survey type.',
    },
    thankYou: {
      title: 'Thank you!',
      body: 'We received your response. Our team will contact you soon.',
      hotLead: 'You are interested in Development Express — we will call you soon!',
    },
    offlineSaved: 'No network — form saved, will auto-send when online ✓',
    pendingSync: '⏳ {count} pending sync',
    questions: {
      owner: ownerQuestionsEn,
      client: clientQuestionsEn,
      pm: pmQuestionsEn,
      contractor: contractorQuestionsEn,
    },
  },
};

export function getQuestionIds(surveyType) {
  if (surveyType === 'client') return CLIENT_QUESTION_IDS;
  if (surveyType === 'pm') return PM_QUESTION_IDS;
  if (surveyType === 'contractor') return CONTRACTOR_QUESTION_IDS;
  return OWNER_QUESTION_IDS;
}

export function getQuestionsForType(lang, surveyType) {
  const resolved = resolveSurveyLang(lang);
  const t = surveyTranslations[resolved] || surveyTranslations.mr;
  // EN base fills ST4 profession pains until HI/MR land
  if (surveyType === 'client') return { ...clientQuestionsEn, ...(t.questions.client || {}) };
  if (surveyType === 'pm') return t.questions.pm || pmQuestionsEn;
  if (surveyType === 'contractor') return t.questions.contractor || contractorQuestionsEn;
  return t.questions.owner;
}
